import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePublicarExemplos } from './utils/exemplosPessoais';
import CaixaDoBanco from './components/CaixaDoBanco';
import MesDaTela from './components/MesDaTela';
import { getEstadoDoBanco, getCaixaDoBanco, CaixaDoBancoDados } from './services/api';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { enviarReciboPendente } from './utils/reciboDoAviso';
import LoginPage from './components/LoginPage';
import DefinirSenha from './components/DefinirSenha';
import TrocarSenha from './components/TrocarSenha';
import { LOGIN_COM_SENHA } from './utils/loginComSenha';
import { ComeceAquiModal, ComeceAquiCard } from './components/ComeceAqui';
import TutorialModal from './components/TutorialModal';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import TransactionForm from './components/TransactionForm';
import TransactionList from './components/TransactionList';
import Projections from './components/Projections';
import PaymentMethods from './components/PaymentMethods';
import NossaConta from './components/NossaConta';
import Reminders from './components/Reminders';
import Investments from './components/Investments';
import Debts from './components/Debts';
import AdvisorChat from './components/AdvisorChat';
import RestartModal from './components/RestartModal';
import WhatsappModal, { buscarWhatsapp } from './components/WhatsappModal';
import ConectorModal, { buscarConector } from './components/ConectorModal';
import NewsletterModal, { buscarNewsletter } from './components/NewsletterModal';
import { consumirAbrir } from './utils/abrirDoLink';
import { trilhaDe, passosDoComece, progresso, PERGUNTA_AO_FINN, AcaoDoPasso } from './utils/comeceAqui';
import { getComeceAqui, marcarComeceAberto, dispensarComeceAqui, EstadoDoComece } from './services/api';
import PaginaDeSaude, { CAMINHO_DA_SAUDE, ehPaginaDeSaude } from './components/PaginaDeSaude';
import CompartilharRelatorio, { buscarCompartilhamentos } from './components/CompartilharRelatorio';
import ModalDeInstalar from './components/ModalDeInstalar';
import ModalDeAvisos from './components/ModalDeAvisos';
import HorarioDoAviso, { buscarHorarioDoAviso } from './components/HorarioDoAviso';
import { useInstalacao, recusouRecentemente, ondeInstala } from './utils/instalarApp';
import { useAvisosNoCelular } from './utils/avisosNoCelular';
import { MASKED_VALUE, formatBRL } from './utils/format';
import { estadoDeLembretes } from './utils/confirmacao';
import { Reminder } from './types/Reminder';
import Goals from './components/Goals';
import CategoryChart from './components/CategoryChart';
import SortableSection, { abrirSecao } from './components/SortableSection';
import MobileLayoutModal from './components/MobileLayoutModal';
import {
  Eye, EyeOff,
  PenLine, CreditCard, TrendingUp, Landmark, Target, LineChart, Bell, PieChart, Receipt,
  Home, Coins,
} from 'lucide-react';
import { OutrasMoedas } from './components/OutrasMoedas';
import { EstadoDaMoeda, SEM_MOEDA } from './types/Moeda';
import { naMoeda, nomeDaMoeda } from './utils/moeda';
import type { LucideIcon } from 'lucide-react';
import ThemePicker, { THEMES, Theme, applyTheme, aplicarTemaSalvo, THEME_KEY, DARK_KEY } from './components/ThemePicker';
import { Transaction, TransactionType } from './types/Transaction';
import { transactionsInMonth, valorExibido } from './utils/monthView';
import { PaymentMethod } from './types/PaymentMethod';
import { Investment } from './types/Investment';
import { Category } from './types/Category';
import { mensagemDeErro } from './utils/mensagemDeErro';
import { observarVoltaDaAba } from './utils/revalidarNoFoco';
import { useSaidaDaCarga } from './utils/saidaDaCarga';
import { estadoDeCarga } from './utils/carregamento';
import { SESSAO_FALSA_ATIVA } from './dev/sessaoFalsa';
import { MODO_DEMO, ORDEM_DA_DEMO, LARGURAS_DA_DEMO } from './demo/modo';
import TelaDeCarga from './components/TelaDeCarga';
import PortaoDoPlano, { useSituacaoDoPlano } from './components/PortaoDoPlano';
import MeuPlanoModal, { buscarMeuPlano } from './components/MeuPlanoModal';
import { deveIrParaConheca, lerArmazenamento, marcarQueJaConhece } from './utils/primeiraVisita';
import DicaFaixa from './components/DicaFaixa';
import Toast from './components/Toast';
import { ehDuplicataPossivel, ehLancamentoCancelado, lancamentoCancelado } from './utils/duplicata';
import { ProvedorDeConfirmacao, useConfirmar } from './contexts/Confirmacao';
import { ProvedorDeVoltar, useFechavelComVoltar } from './contexts/VoltarDoCelular';
import { useFecharAoClicarFora } from './utils/fecharAoClicarFora';
import EdicaoModal from './components/EdicaoModal';
import { useTelaEstreita } from './utils/telaEstreita';
import { temPontoNoAvatar, rotuloDoAvatar } from './utils/pontoDoAvatar';
import {
  getTransactions, createTransaction, updateTransaction, deleteTransaction,
  getPaymentMethods, getInvestments, getCategories,
  setBalanceAnchor, saveParsedInvestment,
  getAnalysisWindow, AnalysisWindow,
  getSafeToSpend, SafeToSpend, getMoedas, definirMoedaAtiva,
  getMonitorAccess,
  getReminders,
  getSaldoDoMes, SaldoDoMes,
  registrarCliqueDeAviso,
  lerPortaDoFinn, adiarPerguntaDoFinn, PortaDoFinn,
  informarValorDoMes, desfazerValorDoMes, marcarFaturaPaga, desmarcarFaturaPaga,
} from './services/api';

/*
 * O tema, aplicado no IMPORT.
 *
 * ── Por que aqui, e não dentro de um componente ───────────────────────────
 *
 * Ele morava em `AppContent`, que só monta depois da sessão. A tela de LOGIN
 * renderiza antes disso — então ela nunca via a cor escolhida nem o modo
 * escuro, e quem tinha o app em verde entrava por uma porta roxa cravada no
 * CSS.
 *
 * Tentei primeiro dentro de `App()`, guardado por `useRef`. Não dá: `App()`
 * tem um `return` antecipado enquanto a sessão carrega, então o hook ficaria
 * condicional e a ordem mudaria entre renders. O `tsc` passou; quem pegou foi
 * o eslint, com `react-hooks/rules-of-hooks`.
 *
 * Escopo de módulo é o lugar certo pelo que o tema É: inline styles no
 * `:root`, um global do documento, não estado de componente. Rodar no import
 * também é o mais cedo possível — nem a tela de carregamento pisca claro
 * antes de virar escuro.
 *
 * `AppContent` continua chamando quando monta. Idempotente: mesmos valores.
 */
aplicarTemaSalvo();

/* ─── Section metadata ────────────────────────────────────────────────── */
const SECTION_META: Record<string, { label: string; full: boolean }> = {
  'sec-form':          { label: 'Nova Transação',   full: false },
  // Só aparece para quem tem moeda estrangeira (13/10): ver `SEC_MOEDAS`.
  'sec-moedas':        { label: 'Outras moedas',     full: false },
  'sec-methods':       { label: 'Métodos',           full: false },
  'sec-investments':   { label: 'Investimentos',     full: false },
  'sec-debts':         { label: 'Dívidas',          full: false },
  'sec-goals':         { label: 'Metas',             full: false },
  'sec-projections':   { label: 'Projeções',         full: true  },
  'sec-reminders':     { label: 'Lembretes',         full: false },
  'sec-casa':          { label: 'Nossa conta',       full: false },
  'sec-chart':         { label: 'Categorias',        full: false },
  'sec-transactions':  { label: 'Transações',        full: true  },
};

const DEFAULT_ORDER = Object.keys(SECTION_META);

/**
 * A seção de moeda estrangeira, a única que nem todo mundo vê: ela existe só
 * para quem comprou moeda. Fica na ordem de todos, e o que muda é se é
 * desenhada. Para quem já tinha a ordem salva ela entra logo depois do
 * formulário, e não no rodapé, que é onde `loadOrder` põe seção nova.
 */
const SEC_MOEDAS = 'sec-moedas';
const comMoedas = (ordem: string[]): string[] => {
  if (ordem.includes(SEC_MOEDAS)) return ordem;
  const i = ordem.indexOf('sec-form');
  return [...ordem.slice(0, i + 1), SEC_MOEDAS, ...ordem.slice(i + 1)];
};
const STORAGE_KEY = 'wallet-board-order';
const FULL_MAP_KEY    = 'wallet-board-full';

/*
 * Quem entra pela primeira vez abre com a arrumação da demonstração (22/10,
 * pedido dela): é a que mostra melhor o app. Antes o padrão de conta nova era
 * a ordem de `SECTION_META`, que continua sendo a ordem do menu.
 */
const ORDEM_INICIAL = ORDEM_DA_DEMO.filter(id => id in SECTION_META);
const missingFrom = (ordem: string[]) => DEFAULT_ORDER.filter(id => !ordem.includes(id));

function loadOrder(): string[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed: string[] = JSON.parse(saved);
      const valid = comMoedas(parsed.filter(id => id in SECTION_META));
      return [...valid, ...missingFrom(valid)];
    }
  } catch {}
  return [...ORDEM_INICIAL, ...missingFrom(ORDEM_INICIAL)];
}

function loadFullMap(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  DEFAULT_ORDER.forEach(id => { defaults[id] = SECTION_META[id].full; });
  try {
    const saved = localStorage.getItem(FULL_MAP_KEY);
    if (saved) return { ...defaults, ...JSON.parse(saved) };
  } catch {}
  return { ...defaults, ...LARGURAS_DA_DEMO };
}


const NAV_ITEMS = DEFAULT_ORDER.map(id => ({ label: SECTION_META[id].label, id }));

/**
 * O ícone de cada seção, para o menu do celular.
 *
 * Nove rótulos de texto em duas colunas obrigam a LER cada um para achar o
 * que se quer. Com ícone, a segunda visita já é reconhecimento — e é a
 * segunda visita que acontece todo dia.
 *
 * Fica aqui, ao lado de `SECTION_META`, e não dentro do menu: quem
 * acrescentar uma seção vai mexer nesta região do arquivo, e um mapa em outro
 * canto é o tipo de coisa que fica para trás.
 */
const SECTION_ICON: Record<string, LucideIcon> = {
  'sec-form':         PenLine,
  'sec-moedas':       Coins,
  'sec-methods':      CreditCard,
  'sec-investments':  TrendingUp,
  'sec-debts':        Landmark,
  'sec-goals':        Target,
  'sec-projections':  LineChart,
  'sec-reminders':    Bell,
  'sec-casa':         Home,
  'sec-chart':        PieChart,
  'sec-transactions': Receipt,
};

function scrollTo(id: string) {
  abrirSecao(id);
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ─── App ─────────────────────────────────────────────────────────────── */
/**
 * Liga o voltar do celular à caixa de confirmação.
 *
 * Existe porque `App` está FORA do `ProvedorDeConfirmacao` e não pode chamar
 * `useConfirmar()`. Uma casca de três linhas dentro do provedor resolve, e
 * mantém o `VoltarDoCelular` sem importar o `Confirmacao` — ver a nota lá.
 */
function VoltarLigado({ children }: { children: React.ReactNode }) {
  const confirmar = useConfirmar();
  return <ProvedorDeVoltar confirmar={confirmar}>{children}</ProvedorDeVoltar>;
}

function App() {
  const { session, loading: authLoading, signOut, user, recuperandoSenha } = useAuth();
  /*
   * O tour de cada tela (22/10): não abre mais sozinho. Quem abre sozinho, uma
   * vez, é o "Comece por aqui", e o tour virou o link no fim dele.
   */
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (!session || !user) return;
    /* A sessão de desenvolvimento não tem conta para registrar, e esta é a
       única chamada do app que usa `fetch` direto em vez do axios — então ela
       escapava do adapter e era a única a bater na rede. */
    if (SESSAO_FALSA_ATIVA) return;
    // A demonstração não registra ninguém — e esta chamada usa `fetch` direto,
    // então escaparia da camada de rede que barra tudo em `/demo`.
    if (MODO_DEMO) return;
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/users/onboard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        email: user.email,
        name: user.user_metadata?.full_name ?? user.user_metadata?.name ?? '',
        avatar_url: user.user_metadata?.avatar_url ?? '',
      }),
    })
      .then(r => r.json())
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]);

  /*
   * O recibo do clique na notificação.
   *
   * O `?aviso=` foi capturado no `index.tsx`, antes de React montar — porque o
   * login do Google volta para `origin` e descarta a query string. Aqui, com a
   * sessão pronta, ele finalmente pode ser mandado com o token.
   *
   * Depende de `session`, e não de montagem: quem tocou na notificação
   * deslogada passa pelo login inteiro antes de chegar neste ponto, e a
   * pendência esperou no `sessionStorage` o tempo todo.
   */
  /*
   * O token sai para uma variável, e o efeito depende DELE.
   *
   * A lista de dependências era `[session?.access_token]` e o corpo lia
   * `session` — o `react-hooks/exhaustive-deps` acusa dependência faltando, e
   * a Vercel roda com `CI=true`, que trata aviso como erro. O build do front
   * parou por causa disto, e não por causa de nada que ele fazia errado.
   *
   * A troca preserva o gatilho exato: o efeito continua disparando quando o
   * token muda, nem mais nem menos. Só a guarda mudou de "tem sessão" para
   * "tem token" — e sem token o envio iria tomar 401 de qualquer forma.
   */
  const tokenDaSessao = session?.access_token;
  // Quem já entrou no app nunca mais é mandado para a /conheca ao sair.
  useEffect(() => { if (tokenDaSessao) marcarQueJaConhece(); }, [tokenDaSessao]);
  useEffect(() => {
    if (!tokenDaSessao) return;
    enviarReciboPendente(registrarCliqueDeAviso);
  }, [tokenDaSessao]);

  /*
   * A MESMA tela do carregamento de dados.
   *
   * Antes eram duas: aqui um anel cinza com "Carregando...", e depois um
   * overlay roxo com o logo. Num refresh estando logada, ela via as duas em
   * sequência — duas esperas com desenhos diferentes parecem duas falhas.
   *
   * Sem barra de progresso: aqui é uma requisição só, que volta ou não volta.
   * Contar etapas seria inventar etapas.
   */
  if (authLoading) return <TelaDeCarga mensagem="Entrando na sua conta..." />;

  if (!session) {
    // A primeira visita vai para a /conheca, não para o login (22/10): ver `utils/primeiraVisita.ts`.
    if (!MODO_DEMO && deveIrParaConheca(window.location, lerArmazenamento)) {
      window.location.replace('/conheca');
      return <TelaDeCarga mensagem="Abrindo..." />;
    }
    return <LoginPage />;
  }

  /*
   * Chegou pelo link de "esqueci minha senha" (05/10): há sessão, mas o que
   * ela veio fazer é escolher a senha. Antes do app, e sem atalho para ele.
   */
  if (recuperandoSenha) return <DefinirSenha />;

  /*
   * A saúde do app (22/10): página própria, sem o painel por trás. Fora do
   * portão do plano porque é da administração, que não paga. Logada e sem
   * ser admin, a própria página diz que não é para ela.
   */
  if (ehPaginaDeSaude(window.location.pathname)) {
    return <ProvedorDeConfirmacao><PaginaDeSaude /></ProvedorDeConfirmacao>;
  }

  return (
    /*
     * O provedor envolve TUDO que está logado, inclusive os modais: a
     * confirmação de excluir uma categoria nasce de dentro do formulário de
     * transação, e a de desvincular o WhatsApp de dentro do modal dele.
     *
     * O portão do plano (20/10) vem antes de tudo: sem plano, a tela de
     * planos fica no lugar do app.
     */
    <PortaoDoPlano onSair={signOut}>
    <ProvedorDeConfirmacao>
      <VoltarLigado>
      <AppContent
        user={user}
        onSignOut={signOut}
        onShowTutorial={() => setShowTutorial(true)}
      />
      {showTutorial && (
        <TutorialModal showIntro onClose={() => setShowTutorial(false)} />
      )}
      </VoltarLigado>
    </ProvedorDeConfirmacao>
    </PortaoDoPlano>
  );
}

function AppContent({ user, onSignOut, onShowTutorial }: { user: any; onSignOut: () => void; onShowTutorial: () => void }) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const telaEstreita = useTelaEstreita();
  const confirmar = useConfirmar();
  const [projectionRefreshTrigger, setProjectionRefreshTrigger] = useState(Date.now());
  const [selectedProjectionMonth, setSelectedProjectionMonth] = useState<{ key: string; label: string } | null>(null);
  const [allTransactionsMode, setAllTransactionsMode] = useState(false);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'all' | TransactionType>('all');
  /* O toque em "a pagar" / "a receber" embaixo do saldo filtra a lista (15/09). */
  const [filtroAguardando, setFiltroAguardando] = useState<{ tipo: 'pagar' | 'receber'; em: number } | null>(null);
  const verAguardando = (tipo: 'pagar' | 'receber') => {
    setAllTransactionsMode(false);
    setFiltroAguardando({ tipo, em: Date.now() });
    scrollTo('sec-transactions');
  };
  /* O toque no card de um método filtra a lista por ele; a edição é o ✎. */
  const [filtroMetodo, setFiltroMetodo] = useState<{ id: string; em: number } | null>(null);
  const verMetodo = (id: string) => {
    setAllTransactionsMode(false);
    setFiltroMetodo({ id, em: Date.now() });
    scrollTo('sec-transactions');
  };
  const [transactionCategoryFilter, setTransactionCategoryFilter] = useState<'all' | string>('all');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Os chips de exemplo dos campos passam a falar com os nomes dela.
  usePublicarExemplos({ transactions, categories, paymentMethods });
  const [paymentMethodsRefresh, setPaymentMethodsRefresh] = useState(Date.now());
  const [debitSourcesRefresh, setDebitSourcesRefresh] = useState(Date.now());
  const [goalsRefreshTrigger, setGoalsRefreshTrigger] = useState(Date.now());

  /*
   * A porta do Finn.
   *
   * `null` = não há o que perguntar (ainda não sei, ou já sei que não). A
   * pergunta só existe quando há portas, então erro de rede vira silêncio em
   * vez de tela quebrada: é uma pergunta, não uma etapa obrigatória.
   *
   * Ela aparece DENTRO da bolha "Perguntar", com o ponto avisando, e não mais
   * num modal que abria sozinho (08/10): quem fechava o tutorial já levava
   * outro modal na cara.
   *
   * ── Quem decide é o SERVIDOR, não o `localStorage` ────────────────────
   *
   * O tutorial usa flag local, e para ele serve. Aqui não: limpar o navegador
   * ou abrir no computador faria o Finn perguntar de novo a quem já respondeu
   * no celular. A pergunta certa é "esta pessoa já declarou?", e só o banco
   * sabe. Ver `docs/designs/finn.md`.
   */
  const [portasDoFinn, setPortasDoFinn] = useState<PortaDoFinn[] | null>(null);

  useEffect(() => {
    let vivo = true;
    lerPortaDoFinn()
      .then(r => { if (vivo && r.precisaPerguntar) setPortasDoFinn(r.portas); })
      .catch(() => {/* silêncio de propósito — ver a nota acima */});
    return () => { vivo = false; };
  }, []);
  const [investmentsRefreshTrigger, setInvestmentsRefreshTrigger] = useState(Date.now());
  const [dataLoading, setDataLoading] = useState(true);
  /* O que JÁ chegou, e há quanto tempo a carga começou. A mensagem sai daí —
     ver `utils/carregamento.ts`. */
  const [prontas, setProntas] = useState<string[]>([]);
  const [decorrido, setDecorrido] = useState(0);

  /*
   * O relógio da carga.
   *
   * Não gira mensagem — só conta o tempo, e serve a uma coisa só: saber
   * quando admitir que o servidor está acordando (`SUSPEITA_DE_SONO_MS`).
   * Quem decide o texto é o que já chegou.
   */
  useEffect(() => {
    if (!dataLoading) return;
    const inicio = Date.now();
    const interval = setInterval(() => setDecorrido(Date.now() - inicio), 200);
    return () => clearInterval(interval);
  }, [dataLoading]);

  /**
   * Link profundo: `https://fintracking.com.br/#sec-debts` abre em Dívidas.
   *
   * O bot do WhatsApp manda esses links quando a ação só dá para fazer aqui
   * (corrigir data, corrigir parcela, conferir dívida). `services/appUrl.ts`
   * no backend é quem os monta, e os ids têm que continuar iguais aos de
   * `SECTION_META` — há teste dos dois lados.
   *
   * Por que não basta o scroll nativo do navegador: quando o hash é lido, a
   * página ainda é o overlay de carregamento. O elemento só existe depois do
   * login e do fetch, e a essa altura o navegador já desistiu. Por isso o
   * scroll espera `dataLoading` virar false.
   *
   * O hash é apagado depois de usado: sem isso, recarregar a página semanas
   * depois pularia para a seção de novo, sem ninguém ter pedido.
   */
  useEffect(() => {
    if (dataLoading) return;
    const id = window.location.hash.slice(1);
    if (!id || !(id in SECTION_META)) return;
    // Um quadro depois, para o board já estar posicionado quando o scroll sai.
    const t = setTimeout(() => {
      scrollTo(id);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }, 0);
    return () => clearTimeout(t);
  }, [dataLoading]);

  /**
   * Tema — cor e claro/escuro.
   *
   * Aplicado de forma SÍNCRONA, antes do primeiro render. Num efeito, o app
   * pintaria claro por um quadro antes de virar escuro — o flash branco que
   * todo modo noturno mal feito tem.
   *
   * Sem preferência salva, segue o sistema operacional: quem já usa o Mac no
   * escuro não deveria precisar pedir de novo aqui.
   */
  const inicial = useRef<{ theme: Theme; dark: boolean } | null>(null);
  if (!inicial.current) inicial.current = aplicarTemaSalvo();

  const [themeId, setThemeId] = useState<string>(inicial.current.theme.id);
  const [dark, setDark] = useState<boolean>(inicial.current.dark);

  const handleThemeSelect = (theme: Theme) => {
    applyTheme(theme, dark);
    setThemeId(theme.id);
    localStorage.setItem(THEME_KEY, theme.id);
  };

  const handleDarkToggle = (escuro: boolean) => {
    const atual = THEMES.find(t => t.id === themeId) ?? THEMES[0];
    applyTheme(atual, escuro);
    setDark(escuro);
    localStorage.setItem(DARK_KEY, String(escuro));
  };

  /**
   * O convite para recomeçar, depois de meses sem lançar.
   *
   * Dispensar é lembrado pelo ÚLTIMO MÊS COM DADO, não por uma flag booleana:
   * assim ele nunca reaparece para o mesmo silêncio, e volta sozinho se ela
   * lançar de novo e sumir outra vez. Um modal que reaparece toda sessão é o
   * jeito mais rápido de ela aprender a fechar sem ler.
   */
  /**
   * O saldo atual — UM número, compartilhado.
   *
   * O card do topo, a faixa e Projeções mostravam três valores diferentes para
   * a mesma pergunta ("quanto eu tenho"), porque cada um calculava do seu
   * jeito. Agora todos leem daqui, e daqui vem de `calculateProjections` — a
   * mesma conta que ela edita em Projeções.
   */
  const [saldo, setSaldo] = useState<SafeToSpend | null>(null);
  /**
   * `true` enquanto o servidor não respondeu.
   *
   * Existe porque o card NÃO pode cair para um cálculo local. Ele caía, e o
   * efeito era pior que a tela vazia que isso queria evitar: `receita − despesa
   * do mês` é um número completamente diferente do saldo acumulado com âncora,
   * então o card mostrava um valor com confiança e trocava por outro meio
   * segundo depois.
   *
   * Número de dinheiro que muda sozinho é pior que número ausente: ela pode
   * ler o primeiro e decidir com base nele.
   */
  const [saldoCarregando, setSaldoCarregando] = useState(true);
  /**
   * Lembretes do mês exibido, só para o ícone de confirmação da lista.
   *
   * `GET /api/reminders?month=` já devolve `transactionId`, `checked` e
   * `createdAt` — tudo que a regra precisa. Nenhum endpoint novo.
   *
   * Falhar aqui não pode derrubar a lista: sem o estado, nenhum ícone
   * aparece e tudo fica como sempre foi.
   */
  const [lembretesDoMes, setLembretesDoMes] = useState<Reminder[]>([]);

  /**
   * Formata um número que pode não ter chegado.
   *
   * Três estados, e os três são honestos: carregando mostra um traço discreto,
   * falha mostra o mesmo traço (o card não some, e ela vê que aquele número
   * não veio), e o valor mostra o valor. Nenhum deles inventa.
   */
  /**
   * A busca das transações falhou — e a tela precisa DIZER isso.
   *
   * O `catch` de `loadTransactions` só escrevia no console, e `transactions`
   * ficava `[]`. Com o servidor fora do ar (o Render free hiberna: é a
   * primeira visita de todo dia), a tela ficava assim:
   *
   *   RECEBIMENTOS R$ 0,00 · GASTOS R$ 0,00
   *   "Nenhuma transação encontrada para Agosto de 2026."
   *
   * Nenhuma das duas coisas era verdade. O app não sabia quanto ela recebeu, e
   * afirmava que foi zero; não sabia se havia transações, e afirmava que não
   * havia. Num app de dinheiro isso não lê como "falhou", lê como "sumiu".
   *
   * O card de Saldo já fazia certo — `fmtServidor` mostra um traço enquanto o
   * número não chega. Recebimentos e Gastos escapavam porque são somados AQUI,
   * a partir da lista: lista vazia por falha e lista vazia por não ter nada
   * são a mesma coisa para quem só olha o `length`.
   */
  const [falhaDeCarga, setFalhaDeCarga] = useState(false);

  const fmtServidor = (v: number | null | undefined): string =>
    hideValues ? maskedValue : (v == null ? '—' : fmt(v));

  /**
   * O número de um card que vem do servidor, com os TRÊS estados à vista.
   *
   * Antes, carregando e "não veio" eram o mesmo traço. Ela apontou: com o
   * servidor lento (o Render free acorda devagar), o card parecia vazio, e não
   * esperando. Agora quem espera é uma barra em movimento, e o traço fica só
   * para o número que de fato não chegou.
   *
   * Com os valores ocultos não há o que esperar na tela: a máscara aparece
   * desde o começo, como sempre.
   */
  const valorDoServidor = (v: number | null | undefined) => (
    saldoCarregando && v == null && !hideValues
      ? (
        <p className="amount amount-carregando" role="status" aria-label="Carregando">
          <span className="amount-barra" aria-hidden="true" />
        </p>
      )
      : <p className="amount">{fmtServidor(v)}</p>
  );

  /**
   * Soma feita AQUI, a partir da lista de transações.
   *
   * Vale a mesma regra do `fmtServidor`: quando a lista não chegou, o total
   * dela não é zero — é desconhecido. Zero é uma afirmação, e afirmar que ela
   * não recebeu nada porque o servidor não respondeu é inventar.
   */
  const fmtLocal = (v: number): string =>
    falhaDeCarga ? fmtServidor(null) : (hideValues ? maskedValue : fmt(v));

  useEffect(() => {
    let vivo = true;
    setSaldoCarregando(true);
    getSafeToSpend()
      .then(r => { if (vivo) setSaldo(r); })
      .catch(() => { /* fica sem número, e o card diz isso */ })
      .finally(() => { if (vivo) setSaldoCarregando(false); });
    return () => { vivo = false; };
  }, [projectionRefreshTrigger]);

  const [janela, setJanela] = useState<AnalysisWindow | null>(null);
  const RECOMECO_KEY = 'wallet-recomeco-dispensado';
  const MESES_PARA_CONVIDAR = 3;

  useEffect(() => {
    let vivo = true;
    getAnalysisWindow()
      .then(j => { if (vivo) setJanela(j); })
      .catch(() => { /* o convite é complemento; o app funciona sem ele */ });
    return () => { vivo = false; };
  }, [projectionRefreshTrigger]);

  const mostrarRecomeco =
    !!janela &&
    janela.analysisFrom === null &&
    janela.staleMonths >= MESES_PARA_CONVIDAR &&
    janela.totalTransactions > 0 &&
    localStorage.getItem(RECOMECO_KEY) !== (janela.lastMonthWithData ?? '');

  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  /*
   * Duas refs, e não uma: o hambúrguer mora DENTRO da barra e o painel é irmão
   * dela. Não existe ancestral comum que sirva de "dentro" sem englobar a
   * página. O gatilho precisa contar como dentro, senão fechar aqui deixa o
   * `onClick` dele reabrir na sequência — o menu piscaria e continuaria aberto.
   */
  const hamburguerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Ocultar valores dos cards — persiste no localStorage
  const [showWhatsapp, setShowWhatsapp] = useState(false);
  const [showConector, setShowConector] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  /*
   * O Open Finance só existe para quem o servidor liberou. A pergunta é feita
   * uma vez, ao entrar, e a resposta decide se o item aparece no menu: para
   * quem está fora, a feature não existe, e um menu que abre numa tela de
   * "não disponível" seria anunciar o que não se pode usar.
   *
   * Falha de rede vira "não disponível": o item some nesta sessão, em vez de
   * aparecer e não funcionar.
   */
  const [bancoDisponivel, setBancoDisponivel] = useState(false);
  /** Quantos movimentos do banco esperam por ela: o contador ao lado do item do menu. */
  const [bancoEsperando, setBancoEsperando] = useState(0);
  /*
   * Sobe a cada volta para a aba, e o efeito abaixo pergunta de novo. Ela pode
   * ter aprovado tudo pelo celular; o ponto no avatar não pode continuar
   * dizendo que tem coisa esperando (19/10).
   */
  const [releituraDoBanco, setReleituraDoBanco] = useState(0);
  useEffect(() => observarVoltaDaAba(() => setReleituraDoBanco(n => n + 1)), []);
  /** A caixa do Open Finance buscada de antemão, para a janela abrir já com ela. */
  const [caixaPrevia, setCaixaPrevia] = useState<CaixaDoBancoDados | null>(null);
  const [showBanco, setShowBanco] = useState(false);
  /*
   * A volta da tela de conexão do provedor (`?banco=voltou`): abre o Open
   * Finance, que é quem confere a sessão e mostra o resultado. Sem isto ela
   * conectaria o banco e cairia na tela inicial sem saber se deu certo.
   */
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('banco') === 'voltou') setShowBanco(true);
  }, []);
  /*
   * O `?abrir=` do email da newsletter (19/10): "Trocar temas" abre a
   * newsletter, e o botão da solução pode abrir o WhatsApp ou o Open Finance.
   * Depois do carregamento, como o hash das seções: antes disso o modal
   * abriria por cima da tela de carregamento.
   */
  useEffect(() => {
    if (dataLoading) return;
    const pedido = consumirAbrir();
    if (pedido === 'newsletter') setShowNewsletter(true);
    else if (pedido === 'whatsapp') setShowWhatsapp(true);
    else if (pedido === 'banco') setShowBanco(true);
  }, [dataLoading]);
  /*
   * Pergunta ao entrar e de novo cada vez que a caixa FECHA: é ao fechar que o
   * número pode ter mudado por algo que ela fez, e um contador que continua
   * dizendo "3" depois de ela resolver os três ensina a ignorá-lo.
   */
  useEffect(() => {
    if (showBanco) return;
    let vivo = true;
    let outraVez: ReturnType<typeof setTimeout> | undefined;
    /*
     * Logo depois de um deploy o servidor ainda está subindo e não sabe dizer
     * se o Open Finance existe para ela. Sem perguntar de novo, o menu ficava
     * escondido até o próximo recarregar. Algumas tentativas, espaçadas, e para.
     */
    const perguntar = (restam: number) => getEstadoDoBanco().then(e => {
      if (!vivo) return;
      if (e.iniciando && restam > 0) { outraVez = setTimeout(() => perguntar(restam - 1), 8000); return; }
      // Com a porta aberta o menu aparece também para quem ainda vai PEDIR o acesso.
      setBancoDisponivel(e.disponivel || !!e.oferta);
      setBancoEsperando(e.esperando ?? 0);
      /*
       * A caixa já vem carregada (08/10). Abrir o Open Finance mostrava
       * "Carregando…" todas as vezes, porque a lista só era pedida com a
       * janela aberta. Agora ela é buscada aqui, junto com o contador — ao
       * entrar e a cada vez que a janela fecha — e a janela abre com ela.
       *
       * Mora no estado do app, e não num cache solto: sai junto com a sessão,
       * e a lista de uma conta nunca aparece para a próxima que entrar.
       */
      if (e.disponivel) getCaixaDoBanco().then(c => { if (vivo) setCaixaPrevia(c); }).catch(() => {});
      else setCaixaPrevia(null);
    }).catch(() => {});
    perguntar(5);
    return () => { vivo = false; if (outraVez) clearTimeout(outraVez); };
  }, [showBanco, releituraDoBanco]);
  const [showMeuPlano, setShowMeuPlano] = useState(false);
  const situacaoDoPlano = useSituacaoDoPlano();
  const [showCompartilhar, setShowCompartilhar] = useState(false);
  const [showTrocarSenha, setShowTrocarSenha] = useState(false);

  /*
   * O convite para instalar o app na tela inicial.
   *
   * ── Quando ele aparece ─────────────────────────────────────────────────
   *
   * Só quando as quatro coisas valem ao mesmo tempo: dá para instalar (ou é
   * iPhone, onde dá para ensinar), a tela é de celular, ela não recusou nos
   * últimos 30 dias, e o app não está instalado.
   *
   * ── Por que não "assim que loga" ───────────────────────────────────────
   *
   * Porque não dá. O Chrome só entrega o evento de instalação depois de a
   * pessoa ter tocado na página e passado 30 segundos nela — pelos critérios
   * dele, não por escolha nossa. Um modal disparado no login encontraria o
   * evento ausente e mostraria um botão que não instala.
   *
   * Então o gatilho é o EVENTO, não o login: quando o navegador avisa que dá,
   * o convite aparece. No iPhone, onde não há evento, ele aparece assim que a
   * tela monta.
   *
   * ── Por que só no celular ──────────────────────────────────────────────
   *
   * Foi o pedido, e faz sentido: no computador o app já vive numa aba, e o
   * ganho de ter um ícone é bem menor que na tela inicial de um telefone.
   */
  const instalacao = useInstalacao();

  /*
   * Os avisos no celular.
   *
   * Ligar aqui SILENCIA o WhatsApp e o email para os quatro alertas: a regra é
   * um aviso por evento, pelo canal mais próximo (`escadaDeCanais.ts` no
   * servidor). Não é acumular canais, é trocar.
   */
  const avisos = useAvisosNoCelular();

  /*
   * "celular" ou "computador" — o app instala nos dois, e os rótulos precisam
   * dizer o aparelho certo. Ver a nota em `instalarApp.ts`.
   */
  const aparelho = ondeInstala();
  const [convitePodeAparecer] = useState(() => !recusouRecentemente());
  const [conviteFechado, setConviteFechado] = useState(false);
  /*
   * O convite aberto POR ELA, no menu.
   *
   * Existe porque a recusa dura 30 dias, e quem muda de ideia no dia seguinte
   * ficava sem caminho: instalar exigiria achar a opção no menu do navegador,
   * que quase ninguém sabe que existe.
   *
   * Separado do automático porque fechar este NÃO conta como recusa — ela
   * pediu para ver.
   */
  const [convitePedido, setConvitePedido] = useState(false);

  /*
   * "Comece por aqui" (22/10) — docs/designs/comece-por-aqui.md.
   *
   * O servidor diz o que está feito; a trilha sai do plano. Relê ao fechar
   * cada modal que um passo abre e a cada lançamento, para o passo se marcar
   * na frente dela. Sem as colunas no servidor (`marcas: null`), "já abriu" e
   * "dispensou" ficam no navegador.
   */
  const [comece, setComece] = useState<EstadoDoComece | null>(null);
  const [showComece, setShowComece] = useState(false);
  const [perguntaAoFinn, setPerguntaAoFinn] = useState<{ texto: string; em: number } | null>(null);
  const chaveDoComece = (o: 'aberto' | 'dispensado') => `fintracking:comece-${o}:${user?.id ?? ''}`;
  const lerMarcaLocal = (o: 'aberto' | 'dispensado') => {
    try { return localStorage.getItem(chaveDoComece(o)) === '1'; } catch { return false; }
  };
  const gravarMarcaLocal = (o: 'aberto' | 'dispensado') => {
    try { localStorage.setItem(chaveDoComece(o), '1'); } catch { /* sem navegador, sem marca */ }
  };
  const passosDoComeco = comece
    ? passosDoComece(trilhaDe(situacaoDoPlano?.plano, comece.feitos), comece.feitos)
    : null;
  const comeceDispensado = comece ? (comece.marcas ? comece.marcas.dispensado : lerMarcaLocal('dispensado')) : true;

  useEffect(() => {
    if (dataLoading || showComece || showWhatsapp || showBanco) return;
    let vivo = true;
    getComeceAqui().then(e => { if (vivo) setComece(e); }).catch(() => {/* sem a lista, o app segue */});
    return () => { vivo = false; };
  }, [dataLoading, showComece, showWhatsapp, showBanco, projectionRefreshTrigger, releituraDoBanco, avisos.estado]);

  /*
   * Abre sozinho UMA vez, e só para quem ainda não lançou nada — o mesmo
   * critério do antigo modal de boas-vindas. Quem já usa o app vê o card no
   * painel, e não um modal no meio do uso.
   */
  const comeceAutomaticoRef = React.useRef(false);
  useEffect(() => {
    if (!comece || !passosDoComeco || comeceAutomaticoRef.current || MODO_DEMO) return;
    comeceAutomaticoRef.current = true;
    const jaAbriu = comece.marcas ? comece.marcas.aberto : lerMarcaLocal('aberto');
    if (jaAbriu || transactions.length > 0 || progresso(passosDoComeco).completo) return;
    setShowComece(true);
    gravarMarcaLocal('aberto');
    marcarComeceAberto().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comece]);

  const dispensarComece = () => {
    gravarMarcaLocal('dispensado');
    setComece(c => c && { ...c, marcas: c.marcas && { ...c.marcas, dispensado: true } });
    dispensarComeceAqui().catch(() => {});
  };

  /* Cada passo abre o que já existe. Fecha a lista antes: um modal por vez. */
  const fazerPasso = (acao: AcaoDoPasso) => {
    setShowComece(false);
    switch (acao) {
      case 'banco': setShowBanco(true); break;
      case 'whatsapp': setShowWhatsapp(true); break;
      case 'instalar':
        if (instalacao.instalado) avisos.ligar();
        else if (instalacao.podeInstalar || instalacao.ensinaNoIOS) setConvitePedido(true);
        else setShowComece(true);
        break;
      case 'lembretes': setTimeout(() => scrollTo('sec-reminders'), 50); break;
      case 'metodos': setTimeout(() => scrollTo('sec-methods'), 50); break;
      case 'casa': setTimeout(() => scrollTo('sec-casa'), 50); break;
      case 'lancar': setTimeout(() => scrollTo('sec-form'), 50); break;
      case 'finn': setPerguntaAoFinn({ texto: PERGUNTA_AO_FINN, em: Date.now() }); break;
    }
  };
  /* No computador sem como instalar, o botão não tem o que abrir: o passo diz onde fazer. */
  const observacaoDoComece = !instalacao.instalado && !instalacao.podeInstalar && !instalacao.ensinaNoIOS
    ? { instalar: telaEstreita
        ? 'No menu do navegador, toque em "Adicionar à tela inicial".'
        : 'Abra o FinTracking no navegador do celular e toque em "Adicionar à tela inicial".' }
    : {};
  /* O erro do servidor vai para o toast; ver utils/mensagemDeErro.ts. */
  const [erroToast, setErroToast] = useState<string | null>(null);

  /**
   * Quem vê o monitor.
   *
   * A pergunta é feita numa rota que responde só sobre quem pergunta — a
   * alternativa seria tentar carregar o monitor inteiro e esconder o menu no
   * 403, pedindo dado de 9 pessoas para descobrir uma coisa sobre si.
   */
  const [ehAdmin, setEhAdmin] = useState(false);
  useEffect(() => {
    let vivo = true;
    getMonitorAccess().then(a => { if (vivo) setEhAdmin(a); });
    return () => { vivo = false; };
  }, []);
  const [hideValues, setHideValues] = useState(() => localStorage.getItem('wallet-hide-values') === 'true');
  const maskedValue = MASKED_VALUE;

  const toggleHideValues = () => {
    setHideValues(v => {
      const next = !v;
      localStorage.setItem('wallet-hide-values', String(next));
      return next;
    });
  };

  // Avatar dropdown
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const avatarRef = React.useRef<HTMLDivElement>(null);

  // WhatsApp, Claude e ChatGPT, Newsletter, Compartilhar relatório e Meu plano
  // são itens desse menu: busca já, para os modais abrirem prontos.
  useEffect(() => {
    if (!avatarMenuOpen || MODO_DEMO) return;
    buscarNewsletter(user?.id).catch(() => {});
    buscarCompartilhamentos(user?.id).catch(() => {});
    buscarMeuPlano(user?.id).catch(() => {});
    buscarWhatsapp(user?.id).catch(() => {});
    buscarConector(user?.id).catch(() => {});
    if (avisos.estado === 'ligado') buscarHorarioDoAviso(user?.id).catch(() => {});
  }, [avatarMenuOpen, user?.id, avisos.estado]);

  /*
   * Era um `useEffect` próprio aqui, com `mousedown`. Virou o mesmo hook dos
   * outros dois painéis: três lugares fazendo a mesma coisa de três jeitos foi
   * o motivo de dois deles não fazerem.
   */
  useFecharAoClicarFora(avatarRef, avatarMenuOpen, () => setAvatarMenuOpen(false));

  useFecharAoClicarFora(
    [hamburguerRef, menuRef],
    mobileMenuOpen,
    React.useCallback(() => setMobileMenuOpen(false), []),
  );

  const handleNavClick = (id: string) => {
    scrollTo(id);
    setMobileMenuOpen(false);
  };

  // Board state
  const [boardOrder, setBoardOrder] = useState<string[]>(loadOrder);
  const [fullMap, setFullMap] = useState<Record<string, boolean>>(loadFullMap);
  const [editMode, setEditMode] = useState(false);
  const [mobileLayoutOpen, setMobileLayoutOpen] = useState(false);
  const [, setActiveId] = useState<string | null>(null);

  const isMobile = () => window.innerWidth <= 768;

  /*
   * O modo edição só GRAVA em "Concluir" (05/10).
   *
   * Cada arrasto escrevia direto no `localStorage`. O botão "Concluir" não
   * concluía nada — só fechava o modo —, e quem mexia para experimentar e
   * recarregava a página encontrava a bagunça salva. Faltava também o caminho
   * de volta: não havia como dizer "deixa como estava".
   *
   * Agora a edição mexe só na tela. Ao entrar, guarda-se o layout como estava;
   * "Concluir" grava o novo, e o ✕ (ou o voltar do celular, ou recarregar)
   * devolve o guardado.
   */
  const antesDeEditar = React.useRef<{ ordem: string[]; cheias: Record<string, boolean> } | null>(null);

  const entrarNaEdicao = () => {
    antesDeEditar.current = { ordem: boardOrder, cheias: fullMap };
    setEditMode(true);
  };

  const concluirEdicao = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(boardOrder));
      localStorage.setItem(FULL_MAP_KEY, JSON.stringify(fullMap));
    } catch { /* sem armazenamento, o layout vale até recarregar */ }
    antesDeEditar.current = null;
    setEditMode(false);
  };

  const cancelarEdicao = React.useCallback(() => {
    const antes = antesDeEditar.current;
    if (antes) {
      setBoardOrder(antes.ordem);
      setFullMap(antes.cheias);
    }
    antesDeEditar.current = null;
    setEditMode(false);
  }, []);

  const handleLayoutButtonClick = () => {
    if (isMobile()) {
      setMobileLayoutOpen(true);
    } else if (editMode) {
      concluirEdicao();
    } else {
      entrarNaEdicao();
    }
  };

  const handleMobileLayoutApply = (newOrder: string[]) => {
    // Quem não vê a seção de moedas reordena sem ela: ela volta ao lugar.
    const ordem = comMoedas(newOrder);
    setBoardOrder(ordem);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ordem));
  };

  /*
   * Outras moedas (13/10). O estado vem do servidor e é relido com o mesmo
   * gatilho do saldo: comprar e gastar na moeda mexem nos dois. Falhar aqui
   * não derruba nada, a seção só não aparece.
   */
  const [moedas, setMoedas] = useState<EstadoDaMoeda>(SEM_MOEDA);
  /** Ela tocou em "Comprou moeda para viajar?": a seção aparece antes da primeira compra. */
  const [abrirMoedas, setAbrirMoedas] = useState(false);
  useEffect(() => {
    let vivo = true;
    getMoedas()
      .then(m => { if (vivo) setMoedas(m.disponivel ? m : SEM_MOEDA); })
      .catch(() => { if (vivo) setMoedas(SEM_MOEDA); });
    return () => { vivo = false; };
  }, [projectionRefreshTrigger]);
  const mostrarMoedas = moedas.disponivel && (moedas.carteiras.length > 0 || abrirMoedas);
  const secoes = boardOrder.filter(id => id !== SEC_MOEDAS || mostrarMoedas);
  const itensDoMenu = NAV_ITEMS.filter(item => item.id !== SEC_MOEDAS || mostrarMoedas);
  const carteiraAtiva = moedas.ativa ? moedas.carteiras.find(c => c.moeda === moedas.ativa) : undefined;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthLabel = now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const currentMonthLabelCap = currentMonthLabel.charAt(0).toUpperCase() + currentMonthLabel.slice(1);
  const activeMonth = selectedProjectionMonth ?? { key: currentMonthKey, label: currentMonthLabelCap };

  /*
   * Os lembretes do mês exibido, para o ícone de confirmação da lista.
   *
   * Recarrega quando o mês muda E quando a projeção é invalidada — marcar um
   * lembrete como feito precisa refletir na lista sem um F5.
   *
   * Falha vira lista vazia de propósito: sem estado, nenhum ícone aparece e a
   * tela fica exatamente como era. Insight de confirmação é enfeite; ele não
   * pode derrubar a lista de lançamentos.
   */
  useEffect(() => {
    let vivo = true;
    getReminders(activeMonth.key)
      .then(r => { if (vivo) setLembretesDoMes(r); })
      .catch(() => { if (vivo) setLembretesDoMes([]); });
    return () => { vivo = false; };
  }, [activeMonth.key, projectionRefreshTrigger]);

  /*
   * Desde 15/09 o estado também sabe do valor do mês informado (nas
   * transações) e da fatura paga (nos métodos): toda mensal e toda compra em
   * cartão com fatura aparece "aguardando" até ser confirmada.
   */
  const confirmacao = useMemo(
    () => estadoDeLembretes(lembretesDoMes, activeMonth.key, { transactions, paymentMethods }),
    [lembretesDoMes, activeMonth.key, transactions, paymentMethods],
  );

  /**
   * Saldo do mês selecionado, quando não é o corrente.
   *
   * Recebimentos e Gastos já respondem ao mês. O saldo ficava preso em hoje, e
   * ela apontou a incoerência: "não faz sentido gastos e recebimentos serem do
   * mês selecionado mas o saldo do mês vigente".
   *
   * `null` no mês corrente (aí vale o saldo de hoje) e fora do horizonte
   * projetado. Falha vira `null` também: o card volta a mostrar o de hoje em
   * vez de um traço, porque um número certo do mês errado é melhor que nenhum
   * número — e o TÍTULO diz de qual mês é.
   */
  const [saldoMesSel, setSaldoMesSel] = useState<SaldoDoMes | null>(null);
  useEffect(() => {
    // `saldo.month` vem do SERVIDOR: montar "hoje" aqui daria uma segunda
    // resposta para qual é o mês corrente, e as duas divergiriam na virada.
    if (!saldo || activeMonth.key === saldo.month) { setSaldoMesSel(null); return; }
    let vivo = true;
    getSaldoDoMes(activeMonth.key)
      .then(r => { if (vivo) setSaldoMesSel(r); })
      .catch(() => { if (vivo) setSaldoMesSel(null); });
    return () => { vivo = false; };
  }, [activeMonth.key, saldo, projectionRefreshTrigger]);

  /**
   * O mês no título dos cards, quando ela não está no mês corrente.
   *
   * No mês de hoje os títulos ficam como sempre foram — acrescentar "de
   * agosto" ali seria ruído num app que ela abre para ver o hoje.
   *
   * `null` significa "sem sufixo".
   */
  const mesDoTitulo = saldo && activeMonth.key !== saldo.month
    ? activeMonth.label.split(' ')[0].toLowerCase()
    : null;
  const comMes = (base: string, prep: 'de' | 'em' = 'de') =>
    mesDoTitulo ? `${base} ${prep} ${mesDoTitulo}` : base;


  useEffect(() => {
    /*
     * Aqui havia um `minDelay` de 1800ms, e ele era o motivo de o app parecer
     * lento em TODO refresh: mesmo com o dado já em cache, ela esperava 1,8
     * segundo olhando a tela de carregamento. A tela existia para ser vista.
     *
     * Agora cada busca avisa quando chega, e a mensagem é o primeiro pendente
     * — o mesmo texto de antes, só que verdadeiro.
     */
    const marcar = (chave: string) => setProntas(p => (p.includes(chave) ? p : [...p, chave]));
    const avisando = <T,>(chave: string, p: Promise<T>) => p.finally(() => marcar(chave));

    Promise.all([
      avisando('transacoes', getTransactions()),
      avisando('metodos', getPaymentMethods()),
      avisando('investimentos', getInvestments()),
      avisando('categorias', getCategories()),
    ])
      .then(([txs, methods, invs, cats]) => {
        setTransactions(txs);
        setProjectionRefreshTrigger(Date.now());
        setPaymentMethods(methods);
        setInvestments(invs);
        setCategories(cats);
        setFalhaDeCarga(false);
      })
      .catch(e => {
        /*
         * A carga inicial é a que importa aqui: é ela que roda quando o Render
         * free ainda está acordando, ou seja na primeira visita de todo dia.
         *
         * Sem marcar a falha, `transactions` fica `[]` e a tela afirma
         * "RECEBIMENTOS R$ 0,00" e "Nenhuma transação encontrada" — duas
         * coisas que o app não sabe. Num app de dinheiro, isso não lê como
         * "falhou", lê como "sumiu".
         */
        console.error(e);
        setFalhaDeCarga(true);
      })
      .finally(() => setDataLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPaymentMethods = async () => {
    try { setPaymentMethods(await getPaymentMethods()); } catch (e) { console.error(e); }
  };
  /**
   * Recarrega a carteira — a cópia daqui E a da seção de Investimentos.
   *
   * O gatilho é bumpado aqui dentro, e não em cada chamador, porque era
   * exatamente isso que estava faltando: `Investments` mantém a própria lista e
   * só carregava na montagem, então aportar por qualquer caminho atualizava os
   * totais do topo e deixava os cards intactos. Um chamador novo esquecer de
   * avisar a seção é o mesmo bug de volta.
   */
  const loadInvestments = async () => {
    try {
      setInvestments(await getInvestments());
      setDebitSourcesRefresh(Date.now());
      setInvestmentsRefreshTrigger(Date.now());
    } catch (e) { console.error(e); }
  };
  const loadCategories = async () => {
    try { setCategories(await getCategories()); } catch (e) { console.error(e); }
  };
  const loadTransactions = async () => {
    try {
      const data = await getTransactions();
      setTransactions(data);
      setFalhaDeCarga(false);
      setProjectionRefreshTrigger(Date.now());
    } catch (e) {
      console.error(e);
      setFalhaDeCarga(true);
    }
  };

  /**
   * Voltar para a aba busca de novo.
   *
   * O WhatsApp escreve: lança transação, marca lembrete como pago, cria
   * limite. Sem isto, ela marca a conta pelo celular, volta para o navegador
   * que estava aberto do lado, e vê o estado velho — que foi exatamente o
   * relato.
   *
   * `loadTransactions` já sobe o `projectionRefreshTrigger`, então uma
   * chamada arrasta saldo, lembretes e confirmação junto.
   *
   * A ref existe porque `loadTransactions` é redefinida a cada render: pôr
   * ela nas dependências religaria o ouvinte a cada render, e o piso de tempo
   * (que mora no hook) nunca chegaria a segurar nada.
   */
  const revalidarRef = React.useRef(loadTransactions);
  revalidarRef.current = loadTransactions;
  useEffect(() => observarVoltaDaAba(() => { revalidarRef.current(); }), []);

  /**
   * Cria o lançamento — perguntando antes, se parecer repetido (05/10).
   *
   * O servidor confere antes de gravar. Avisa, não bloqueia: duas compras
   * iguais no mesmo dia existem. Se ela confirmar, o segundo envio vai sem a
   * conferência; se desistir, rejeita com `lancamentoCancelado`, para o
   * formulário não limpar o que ela digitou.
   *
   * Fora de `handleSubmit` de propósito: lá dentro só existe UM caminho de
   * criação, e `ConfirmarMes.test.tsx` conta as chamadas para garantir que
   * editar nunca cria um segundo registro.
   */
  const criarConferindoDuplicata = async (dados: Omit<Transaction, 'id' | 'createdAt'>) => {
    try {
      await createTransaction(dados, { verificarDuplicata: true });
    } catch (e) {
      if (!ehDuplicataPossivel(e)) throw e;
      const lancar = await confirmar({
        titulo: 'Lançar de novo?',
        texto: `Parece que este lançamento já existe: ${e.frase}.`,
        acao: 'Lançar mesmo assim',
      });
      if (!lancar) throw lancamentoCancelado();
      await createTransaction(dados);
    }
  };

  const handleSubmit = async (transactionData: Omit<Transaction, 'id' | 'createdAt'>, scope?: 'this' | 'future') => {
    try {
      if (editingTransaction) {
        if (scope === 'this') {
          /*
           * "Só neste mês" (15/09): o valor novo vira o VALOR DO MÊS exibido, e o
           * registro fica com o valor cadastrado — os outros campos editados vão
           * normalmente. Era o "Apenas esta", que convertia o registro original
           * em avulsa e criava outra mensal: mudava o mês errado e partia a série.
           */
          await updateTransaction(editingTransaction.id, { ...transactionData, value: editingTransaction.value });
          await informarValorDoMes(editingTransaction.id, activeMonth.key, Number(transactionData.value));
        } else {
          // "Daqui pra frente": o valor cadastrado muda — o servidor grava
          // `value_since` e a média recomeça a partir deste mês.
          await updateTransaction(editingTransaction.id, transactionData);
        }
      } else {
        await criarConferindoDuplicata(transactionData);
      }
      await loadTransactions();
      setEditingTransaction(null);
      setProjectionRefreshTrigger(Date.now());
    } catch (e) {
      // Ela escolheu não lançar: rejeita para o formulário não limpar o que
      // foi digitado, mas sem toast — não houve falha nenhuma.
      if (ehLancamentoCancelado(e)) throw e;
      console.error(e);
      setErroToast(mensagemDeErro(e, 'Não consegui salvar a transação.'));
      throw e;
    }
  };

  /* Onde a página estava no instante do toque — ver `EdicaoModal`. */
  const rolagemAoEditar = React.useRef(0);

  const handleEdit = (transaction: Transaction) => {
    rolagemAoEditar.current = window.scrollY;
    setEditingTransaction(transaction);
    /*
     * O salto só faz sentido quando o formulário fica onde estava. No celular
     * ele vai para um modal por cima, e rolar a página por baixo do modal
     * moveria o fundo sem motivo — e deixaria a pessoa noutro lugar ao fechar.
     */
    if (!telaEstreita) scrollTo('sec-form');
  };

  /**
   * Item 17 — ajustar o saldo agora grava um CAMPO, não uma transação fantasma.
   *
   * Antes isto criava uma transação "Ajuste de saldo" datada no último dia do
   * mês anterior, com o primeiro método de pagamento da lista. Ela entrava como
   * despesa/receita real e poluía a análise por categoria e o total do mês — e,
   * depois que o alerta de limite voltou a funcionar (item 6b), teria começado
   * a disparar alerta falso contra o primeiro cartão.
   */
  const handleBalanceAdjust = async (newBalance: number) => {
    const hoje = new Date();
    const iso = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
    try {
      await setBalanceAnchor({ amount: newBalance, date: iso });
      await loadTransactions();
      setProjectionRefreshTrigger(Date.now());
    } catch (e) {
      console.error(e);
      setErroToast(mensagemDeErro(e, 'Não consegui ajustar o saldo.'));
      throw e;
    }
  };

  /*
   * Confirmar o mês (15/09). Recarregar as transações sobe o gatilho da
   * projeção, então saldo, "a pagar" e lembretes vêm junto. Erro sobe para a
   * janela, que o mostra — ela não fecha calada.
   */
  const handleInformarValorDoMes = async (t: Transaction, mes: string, valor: number) => {
    await informarValorDoMes(t.id, mes, valor);
    await loadTransactions();
  };
  const handleDesfazerValorDoMes = async (t: Transaction, mes: string) => {
    await desfazerValorDoMes(t.id, mes);
    await loadTransactions();
  };
  const handleFaturaPaga = async (paymentMethodId: string, mes: string, paga: boolean) => {
    await (paga ? marcarFaturaPaga : desmarcarFaturaPaga)(paymentMethodId, mes);
    await Promise.all([loadPaymentMethods(), loadTransactions()]);
  };

  const handleDelete = async (id: string) => {
    if (await confirmar({
      titulo: 'Excluir este lançamento?',
      texto: 'Ele sai da lista, do saldo e da projeção. Não dá para desfazer.',
      acao: 'Excluir', perigo: true,
    })) {
      try {
        await deleteTransaction(id);
        await loadTransactions();
        setProjectionRefreshTrigger(Date.now());
      } catch (e) {
        console.error(e);
        setErroToast(mensagemDeErro(e, 'Não consegui excluir a transação.'));
      }
    }
  };

  const handleCancelEdit = () => setEditingTransaction(null);

  const handleCategoryChartClick = (categoryId: string) => {
    setTransactionTypeFilter(TransactionType.EXPENSE);
    setTransactionCategoryFilter(categoryId);
    setTimeout(() => {
      document.getElementById('sec-transactions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleProjectionMonthSelect = (monthKey: string, monthLabel: string) => {
    setAllTransactionsMode(false);
    if (selectedProjectionMonth?.key === monthKey) {
      setSelectedProjectionMonth(null);
    } else {
      setSelectedProjectionMonth({ key: monthKey, label: monthLabel });
      setTimeout(() => {
        document.getElementById('sec-transactions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  const goToMonth = (key: string) => {
    setAllTransactionsMode(false);
    if (key === currentMonthKey) {
      setSelectedProjectionMonth(null);
      return;
    }
    const [y, m] = key.split('-').map(Number);
    const label = new Date(y, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    setSelectedProjectionMonth({ key, label: label.charAt(0).toUpperCase() + label.slice(1) });
  };

  const navigateMonth = (direction: 1 | -1) => {
    const baseKey = selectedProjectionMonth?.key ?? currentMonthKey;
    const [y, m] = baseKey.split('-').map(Number);
    const d = new Date(y, m - 1 + direction, 1);
    goToMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  /* ── Column / stretch toggles ── */
  const handleToggleFull = (id: string) => {
    // Sem gravar: quem grava é "Concluir". Ver `concluirEdicao`.
    setFullMap(prev => ({ ...prev, [id]: !prev[id] }));
  };

  /* ── DnD handlers ── */
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      setBoardOrder(prev => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        // Sem gravar: quem grava é "Concluir". Ver `concluirEdicao`.
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  /* ── Derived values ── */
  const filteredTransactions = transactionsInMonth(
    transactions,
    selectedProjectionMonth?.key ?? currentMonthKey
  );

  // Transações debitadas de investimento/reserva não afetam os totais de gastos/saldo
  // Item 9b: lê a coluna, com fallback ao prefixo para linhas antigas.
  const isDebitSource = (t: Transaction) =>
    t.debitDirection ? t.debitDirection === 'debit' : !!t.paymentMethod?.startsWith('__debit__');

  // `valorExibido`: o valor do mês de uma mensal variável, não o cadastrado (15/09).
  const totalIncome  = filteredTransactions.filter(t => t.type === TransactionType.INCOME).reduce((s, t) => s + valorExibido(t), 0);
  const totalExpense = filteredTransactions.filter(t => t.type === TransactionType.EXPENSE && !isDebitSource(t)).reduce((s, t) => s + valorExibido(t), 0);
  // `balance` (receita − despesa do mês) foi removido em 27/08: ele só existia
  // para o card mostrar ALGUM número enquanto o servidor não respondia, e o
  // número era outro. O lint acusou que ele ficou sem uso, o que é a prova de
  // que essa era a única função dele.
  /**
   * "Investido" e o par do saldo: o que saiu do caixa e continua sendo dela.
   * Reserva de meta conta tanto quanto CDB — os dois sao dinheiro guardado, e
   * mostrar so as aplicacoes deixava as reservas invisiveis no topo.
   * Cai no calculo local (so aplicacoes) enquanto o servidor nao responde.
   */
  /**
   * Mesmo motivo do saldo: o cálculo local soma só as aplicações e ignora
   * reserva de meta, então ele mostra menos e depois corrige para mais.
   */
  const totalInvested = saldo?.investedTotal ?? null;
  /**
   * O saldo que o card mostra.
   *
   * No mês corrente: o confirmado (despesa ligada a lembrete e não paga volta,
   * receita não recebida sai). Fora dele: o acumulado projetado daquele mês.
   *
   * `??` entre valores do SERVIDOR, nunca cálculo local — a armadilha 11 é
   * sobre o card cair numa conta própria, não sobre escolher entre campos que
   * o servidor mandou.
   */
  const saldoExibido = saldoMesSel
    ? saldoMesSel.saldo
    : (saldo?.saldoConfirmado ?? saldo?.currentBalance ?? null);
  /**
   * O investido projetado do mês, quando ele existe.
   *
   * `null` no mês corrente (aí vale a foto de hoje) e em meses PASSADOS, onde
   * o app não tem o histórico. Nesses casos o card mostra o valor de hoje com
   * o título sem mês.
   */
  const investidoDoMes = saldoMesSel?.investido ?? null;
  const totalMonthlyReturn = investments.reduce((s, i) => s + i.amount * (i.monthlyRate / 100), 0);
  /* Cru: os chamadores decidem esconder, e todos já passam por
     `hideValues ? maskedValue : fmt(...)`. */
  const fmt = (v: number) => formatBRL(v);

  /* ── Section content renderer ── */
  /*
   * O formulário de lançamento é UM elemento, guardado aqui, e o que muda é
   * ONDE ele é colocado: dentro da seção, ou dentro do modal de edição do
   * celular. Uma segunda cópia "só para editar" seria duas telas que precisam
   * concordar sobre o que é uma transação — e que divergem no primeiro campo
   * novo. Ver `components/EdicaoModal.tsx`.
   */
  const formDeTransacao = (
          <TransactionForm
            onSubmit={handleSubmit}
            editingTransaction={editingTransaction}
            mesExibido={activeMonth.key}
            onCancel={handleCancelEdit}
            /* Criar o lembrete daqui liga o lançamento a ele: a lista precisa
               passar a mostrar o anel, e o saldo precisa recontar. */
            onLembreteCriado={() => setProjectionRefreshTrigger(Date.now())}
            paymentMethodsRefresh={paymentMethodsRefresh}
            debitSourcesRefresh={debitSourcesRefresh}
            categories={categories}
            onCategoryCreated={loadCategories}
            onCategoryDeleted={loadCategories}
            onDebitApplied={() => { loadInvestments(); setGoalsRefreshTrigger(Date.now()); }}
            // "investi 5 mil em CDB" no campo único cai na tabela de
            // investimentos, não em gastos. Quando o dinheiro sai da conta
            // agora, o backend também grava a transação — então a lista de
            // lançamentos e a projeção recarregam junto.
            onCreateInvestment={async (inv) => {
              await saveParsedInvestment(inv);
              loadInvestments();
              setDebitSourcesRefresh(Date.now());
              if (inv.movesCash) {
                loadTransactions();
                setProjectionRefreshTrigger(Date.now());
              }
            }}
          />
  );

  /*
   * No celular, editar abre modal em vez de saltar a página.
   *
   * Antes `handleEdit` chamava `scrollTo('sec-form')`: a página ia lá para
   * cima e ela tinha que reencontrar onde estava depois de salvar. No celular
   * a lista de Transações fica no FIM do painel, então o salto atravessava as
   * nove seções inteiras — duas vezes.
   */
  const editandoEmModal = telaEstreita && !!editingTransaction;

  const renderContent = (id: string): React.ReactNode => {
    switch (id) {
      case 'sec-form':
        /*
         * Enquanto o formulário está no modal, a seção mostra que ele saiu
         * dali. Isto fica ATRÁS do véu e quase ninguém vê — existe para o card
         * não colapsar num retângulo vazio quando o modal fecha por animação.
         */
        return editandoEmModal
          ? <p className="sec-form-no-modal">Editando um lançamento…</p>
          : (
            <>
              {/*
                O modo viagem nunca fica ligado escondido (13/10): enquanto o
                WhatsApp lança em euro, a tela diz. E diz também que ESTE
                formulário segue em real, para ninguém lançar na moeda errada.
              */}
              {carteiraAtiva && (
                <p className="moe-faixa">
                  <span>
                    Modo viagem: pelo WhatsApp seus gastos entram em <strong>{nomeDaMoeda(carteiraAtiva.moeda)}</strong>
                    {!hideValues && ` (restam ${naMoeda(carteiraAtiva.saldo, carteiraAtiva.moeda)})`}. Este formulário lança em real.
                  </span>
                  <button type="button" className="moe-acao" onClick={() => scrollTo(SEC_MOEDAS)}>
                    Gastei em {nomeDaMoeda(carteiraAtiva.moeda)}
                  </button>
                  <button
                    type="button" className="moe-acao"
                    onClick={() => definirMoedaAtiva(null).then(() => setProjectionRefreshTrigger(Date.now())).catch(() => { /* a faixa continua, e ela tenta de novo */ })}
                  >
                    Voltar ao real
                  </button>
                </p>
              )}
              {formDeTransacao}
            </>
          );
      case 'sec-moedas':
        return (
          <OutrasMoedas
            estado={moedas}
            categories={categories}
            hideValues={hideValues}
            onChange={() => { loadTransactions(); setProjectionRefreshTrigger(Date.now()); }}
          />
        );
      case 'sec-casa':
        return (
          <NossaConta
            hideValues={hideValues}
            mes={selectedProjectionMonth?.key}
            /*
             * O mesmo gatilho das projeções.
             *
             * A soma da casa vem de lançamentos, e todo lugar que grava um
             * lançamento já sobe este número — inclusive o WhatsApp, quando a
             * tela recarrega. Sem isto a seção só se atualizava ao trocar de
             * mês: quem marcasse um gasto como da casa via o cartão parado e
             * concluía que a marca não pegou.
             */
            refreshTrigger={projectionRefreshTrigger}
          />
        );
      case 'sec-methods':
        return (
          <>
            <PaymentMethods
              hideValues={hideValues}
              metodosIniciais={paymentMethods}
              refreshTrigger={projectionRefreshTrigger}
              selectedMonth={selectedProjectionMonth}
              onVerTransacoes={verMetodo}
              onMethodsChange={() => { setPaymentMethodsRefresh(Date.now()); loadPaymentMethods(); setProjectionRefreshTrigger(Date.now()); }}
            />
            {/* A porta de entrada de quem ainda não tem moeda: a seção só nasce depois da primeira compra. */}
            {moedas.disponivel && !mostrarMoedas && (
              <button
                type="button" className="moe-acao moe-entrada"
                onClick={() => { setAbrirMoedas(true); setTimeout(() => scrollTo(SEC_MOEDAS), 80); }}
              >
                Comprou moeda para viajar?
              </button>
            )}
          </>
        );
      case 'sec-debts':
        return (
          <Debts
            hideValues={hideValues}
            onChange={() => setProjectionRefreshTrigger(Date.now())}
            transactions={transactions}
            paymentMethods={paymentMethods}
            investments={investments}
            onDinheiroMudou={() => { loadTransactions(); loadInvestments(); }}
            bancoDisponivel={bancoDisponivel}
          />
        );
      case 'sec-investments':
        return (
          <Investments
            hideValues={hideValues}
            refreshTrigger={investmentsRefreshTrigger}
            onInvestmentsChange={loadInvestments}
            // Aportar cria transação (era o item 1 de "Em aberto"): a lista de
            // lançamentos e a projeção precisam recarregar junto.
            onTransactionRecorded={() => { loadTransactions(); setProjectionRefreshTrigger(Date.now()); }}
          />
        );
      case 'sec-goals':
        return (
          <Goals
            hideValues={hideValues}
            filteredTransactions={filteredTransactions}
            categories={categories}
            activeMonthLabel={activeMonth.label}
            onGoalsChange={() => setDebitSourcesRefresh(Date.now())}
            // Item 27: o aporte agora cria transação, então a lista e a
            // projeção precisam recarregar junto.
            onDepositRecorded={() => { loadTransactions(); setProjectionRefreshTrigger(Date.now()); }}
            refreshTrigger={goalsRefreshTrigger}
          />
        );
      case 'sec-projections':
        return (
          <Projections
            hideValues={hideValues}
            refreshTrigger={projectionRefreshTrigger}
            selectedMonth={selectedProjectionMonth?.key ?? null}
            onMonthSelect={handleProjectionMonthSelect}
            onBalanceAdjust={handleBalanceAdjust}
          />
        );
      case 'sec-reminders':
        // As transações vêm do App e não de um fetch próprio: elas já estão
        // carregadas aqui, e buscar de novo pagaria duas vezes pelo mesmo dado.
        return (
          <Reminders
            hideValues={hideValues}
            activeMonth={activeMonth}
            transactions={transactions}
            /*
             * Marcar um lembrete muda DUAS coisas fora daqui: o ícone na
             * lista de Transações e o saldo (o valor entra ou sai conforme a
             * confirmação). Bater no mesmo gatilho da projeção recarrega os
             * dois de uma vez — é o canal que já existe para "o dinheiro
             * mudou".
             */
            paymentMethods={paymentMethods}
            refreshTrigger={projectionRefreshTrigger}
            /* Lembrete de fatura marca a fatura (15/09): os métodos voltam junto,
               senão o ícone das compras do cartão não muda até um F5. */
            onMudou={() => { setProjectionRefreshTrigger(Date.now()); loadPaymentMethods(); setPaymentMethodsRefresh(Date.now()); }}
          />
        );
      case 'sec-chart':
        return (
          <CategoryChart
            hideValues={hideValues}
            /* O mesmo mês do topo: ver a nota em `CategoryChartProps`. */
            onNavigateMonth={navigateMonth}
            onVoltarParaHoje={
              selectedProjectionMonth ? () => setSelectedProjectionMonth(null) : undefined
            }
            transactions={filteredTransactions}
            categories={categories}
            activeMonthLabel={activeMonth.label}
            onCategoryClick={handleCategoryChartClick}
            onCategoriasMudaram={loadCategories}
          />
        );
      case 'sec-transactions':
        return (
          <TransactionList
            hideValues={hideValues}
            falhaDeCarga={falhaDeCarga}
            transactions={allTransactionsMode ? transactions : filteredTransactions}
            // A pergunta escrita não respeita o recorte do mês: "parcelamentos
            // que fiz em dezembro" precisa enxergar dezembro com agosto na tela.
            allTransactions={transactions}
            onEdit={handleEdit}
            onDelete={handleDelete}
            projectionMonthLabel={allTransactionsMode ? null : (selectedProjectionMonth?.label ?? currentMonthLabelCap)}
            onClearProjectionFilter={() => { setSelectedProjectionMonth(null); setAllTransactionsMode(true); }}
            paymentMethods={paymentMethods}
            categories={categories}
            initialTypeFilter={transactionTypeFilter}
            filtroAguardando={filtroAguardando}
            filtroMetodo={filtroMetodo}
            initialCategoryFilter={transactionCategoryFilter}
            confirmacao={confirmacao}
            mesExibido={activeMonth.key}
            onInformarValorDoMes={handleInformarValorDoMes}
            onDesfazerValorDoMes={handleDesfazerValorDoMes}
            onFaturaPaga={handleFaturaPaga}
          />
        );
      default:
        return null;
    }
  };

  /* A tela de carregamento é derivada, não guardada: o que já chegou mais o
     tempo decorrido decidem tudo. Ver `utils/carregamento.ts`. */
  const carga = estadoDeCarga({ prontas, decorridoMs: decorrido });

  /* A tela some desaparecendo — ver `utils/saidaDaCarga.ts`. */
  const saidaDaCarga = useSaidaDaCarga(dataLoading);

  /*
   * O que o voltar fecha, do topo para baixo.
   *
   * A ordem aqui é a de registro, e fecha do último para o primeiro. Os modais
   * se registram sozinhos (montam só quando abertos); estes são estados que
   * vivem no App e por isso passam a bandeira.
   *
   * O mês navegado entra na lista porque voltar dele é literalmente voltar —
   * e é o único jeito de o botão desfazer uma navegação que a pessoa fez.
   */
  useFechavelComVoltar(mobileMenuOpen, React.useCallback(() => setMobileMenuOpen(false), []));
  useFechavelComVoltar(avatarMenuOpen, React.useCallback(() => setAvatarMenuOpen(false), []));
  // O voltar do celular DESCARTA: sair sem concluir não pode salvar.
  useFechavelComVoltar(editMode, cancelarEdicao);
  useFechavelComVoltar(
    !!selectedProjectionMonth,
    React.useCallback(() => setSelectedProjectionMonth(null), []),
  );

  return (
    <div className="App">

      {/*
        ── A tela de carregamento inicial ──

        Ela fica no ar do primeiro quadro até o dado chegar — sem atraso para
        aparecer e sem tempo mínimo para sumir.

        Segurei ela por 250ms numa versão, para carga rápida não piscar. O
        relato desmentiu: "a tela de loading demora para aparecer". No boot a
        tela da SESSÃO já está no ar e esta é a continuação dela; o atraso não
        evitava piscada, abria um buraco em que o app aparecia pela metade.

        A MENSAGEM é o progresso de verdade: ela é a primeira das quatro
        buscas que ainda não voltou. Antes eram cinco frases girando por
        relógio, em laço — depois de 7,5s ela via "Buscando suas transações"
        outra vez tendo já lido "Preparando seu painel". Progresso encenado é
        da mesma família do número escrito pelo modelo: parece informação e
        não é.
      */}
      {/*
        A tela de carga SAI desaparecendo, e não de um quadro para o outro.
        
        Quando o dado chegava, o overlay simplesmente deixava de existir e o
        painel inteiro aparecia de uma vez — nove seções, quatro cards e uma
        rosca colorida surgindo juntos. É o mesmo susto que uma luz acesa de
        repente.
        
        A saída é o único momento em que animar aqui é seguro. A ENTRADA já
        teve animação e foi removida por causar piscada: no boot há três
        renderizações da mesma tela, e a animação de entrada rodava de novo em
        cada montagem, por cima de uma tela que já estava lá. Sair acontece uma
        vez só.
        
        Ver `utils/saidaDaCarga.ts` para por que não é só um `&&`.
      */}
      {saidaDaCarga.visivel && (
        <TelaDeCarga
          mensagem={carga.mensagem}
          acordando={carga.acordando}
          saindo={saidaDaCarga.saindo}
        />
      )}

      <nav className="app-nav">
        {/* Hamburger — mobile only, left side */}
        <button
          ref={hamburguerRef}
          className={`app-nav-hamburger${mobileMenuOpen ? ' open' : ''}`}
          onClick={() => setMobileMenuOpen(v => !v)}
          aria-label="Menu"
          aria-expanded={mobileMenuOpen}
        >
          <span /><span /><span />
        </button>

        <span className="app-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer' }}>
          <span className="logo-text"><span className="logo-fin">Fin</span><span className="logo-track">Tracking</span></span>
        </span>

        {/* Desktop links */}
        <div className="app-nav-links">
          {itensDoMenu.map(item => (
            <button key={item.id} className="app-nav-link" onClick={() => handleNavClick(item.id)}>
              {item.label}
            </button>
          ))}
        </div>

        {/* Actions (always visible) */}
        <div className="app-nav-actions">
          <button
            id="nav-eye-btn"
            className="app-nav-eye-btn"
            onClick={toggleHideValues}
            title={hideValues ? 'Mostrar valores' : 'Ocultar valores'}
            aria-label={hideValues ? 'Mostrar valores' : 'Ocultar valores'}
          >
            {hideValues ? <EyeOff size={24} strokeWidth={1.8} /> : <Eye size={24} strokeWidth={1.8} />}
          </button>
          <span id="nav-theme-picker">
            <ThemePicker
              currentThemeId={themeId}
              onSelect={handleThemeSelect}
              dark={dark}
              onToggleDark={handleDarkToggle}
            />
          </span>
          {/*
            O caminho de volta, ao lado de "Concluir". Só existe durante a
            edição, e diz o que faz — um ✕ sozinho ao lado de um ✓ lê como
            "fechar", e fechar aqui JOGA FORA o que ela arrastou.
          */}
          {editMode && (
            <button
              className="app-nav-edit-cancelar"
              onClick={cancelarEdicao}
              title="Descartar as mudanças e voltar ao layout de antes"
              aria-label="Descartar as mudanças do layout"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          <button
            id="nav-layout-btn"
            className={`app-nav-edit-btn${editMode ? ' active' : ''}`}
            onClick={handleLayoutButtonClick}
            title={editMode ? 'Salvar o layout e sair do modo edição' : 'Editar layout'}
          >
            {editMode ? (
              <>
                <svg className="app-nav-edit-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="app-nav-edit-label">Concluir</span>
              </>
            ) : (
              <>
                <svg className="app-nav-edit-icon" width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <rect x="1" y="1" width="5.5" height="5.5" rx="1.5" fill="currentColor"/>
                  <rect x="8.5" y="1" width="5.5" height="5.5" rx="1.5" fill="currentColor" opacity="0.5"/>
                  <rect x="1" y="8.5" width="5.5" height="5.5" rx="1.5" fill="currentColor" opacity="0.5"/>
                  <rect x="8.5" y="8.5" width="5.5" height="5.5" rx="1.5" fill="currentColor"/>
                </svg>
                <span className="app-nav-edit-label">Layout</span>
              </>
            )}
          </button>

          {/* User avatar + sign out */}
          <div className="app-nav-user" ref={avatarRef}>
            <button
              id="nav-avatar-btn"
              className="app-nav-avatar-btn"
              onClick={() => setAvatarMenuOpen(v => !v)}
              aria-label={rotuloDoAvatar(bancoEsperando)}
            >
              {user?.user_metadata?.avatar_url ? (
                <img
                  className="app-nav-avatar"
                  src={user.user_metadata.avatar_url}
                  alt={user.user_metadata.full_name ?? 'User'}
                />
              ) : (
                <img
                  className="app-nav-avatar"
                  src="/icons/ft-64x64.png"
                  alt="Avatar"
                />
              )}
              {/* Open Finance esperando por ela. O número fica no item do menu. */}
              {temPontoNoAvatar(bancoEsperando) && <span className="app-nav-avatar-ponto" aria-hidden="true" />}
            </button>

            {avatarMenuOpen && (
              <div className="app-nav-avatar-dropdown">
                <div className="app-nav-avatar-info">
                  {user?.user_metadata?.full_name && (
                    <span className="app-nav-avatar-name">{user.user_metadata.full_name}</span>
                  )}
                  <span className="app-nav-avatar-email">{user?.email}</span>
                </div>
                <div className="app-nav-avatar-divider" />
                <button
                  className="app-nav-avatar-menu-item so-com-conta"
                  onClick={() => { setAvatarMenuOpen(false); setShowWhatsapp(true); }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1a6 6 0 00-5.2 9L1 13l3.1-.8A6 6 0 107 1z"
                          stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                  WhatsApp
                </button>

                {/*
                  Conectar o Claude e o ChatGPT: mesma família do WhatsApp e do
                  compartilhar — quem alcança os dados, e por onde. Fica logo
                  depois do WhatsApp porque é a outra forma de usar o app sem
                  abrir o app.
                */}
                {/* A newsletter diária: a outra forma de acompanhar sem abrir o app. */}
                <button
                  className="app-nav-avatar-menu-item so-com-conta"
                  onClick={() => { setAvatarMenuOpen(false); setShowNewsletter(true); }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <rect x="1.5" y="3" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M2 4l5 3.5L12 4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                  </svg>
                  Newsletter diária
                </button>

                <button
                  id="nav-conector-btn"
                  className="app-nav-avatar-menu-item so-com-conta"
                  onClick={() => { setAvatarMenuOpen(false); setShowConector(true); }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="2" fill="currentColor"/>
                    <circle cx="2.4" cy="3.4" r="1.3" stroke="currentColor" strokeWidth="1.1"/>
                    <circle cx="2.4" cy="10.6" r="1.3" stroke="currentColor" strokeWidth="1.1"/>
                    <circle cx="11.6" cy="7" r="1.3" stroke="currentColor" strokeWidth="1.1"/>
                    <path d="M3.6 4.2l1.8 1.5M3.6 9.8l1.8-1.5M9 7h1.3"
                          stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                  </svg>
                  Conectar ao Claude / ChatGPT
                </button>

                {bancoDisponivel && (
                  <button
                    id="nav-banco-btn"
                    className="app-nav-avatar-menu-item so-com-conta"
                    onClick={() => { setAvatarMenuOpen(false); setShowBanco(true); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M1.5 5.2L7 2l5.5 3.2M2.5 5.5v5M5.5 5.5v5M8.5 5.5v5M11.5 5.5v5M1.5 12h11"
                            stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Open Finance
                    {bancoEsperando > 0 && (
                      <span className="app-nav-conta" aria-label={`${bancoEsperando} esperando você`}>
                        {bancoEsperando}
                      </span>
                    )}
                  </button>
                )}

                {/*
                  Compartilhar o relatório fica AQUI, junto do WhatsApp e dos
                  avisos: é do mesmo tipo — quem alcança o quê, e por onde.
                  Não é uma seção do painel porque não se olha todo dia; é uma
                  decisão que se toma uma vez e se revisa de vez em quando.
                */}
                <button
                  className="app-nav-avatar-menu-item so-com-conta"
                  onClick={() => { setAvatarMenuOpen(false); setShowCompartilhar(true); }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="3.5" cy="7" r="1.8" stroke="currentColor" strokeWidth="1.3"/>
                    <circle cx="10.5" cy="3.5" r="1.8" stroke="currentColor" strokeWidth="1.3"/>
                    <circle cx="10.5" cy="10.5" r="1.8" stroke="currentColor" strokeWidth="1.3"/>
                    <path d="M5.1 6.2l3.8-1.9M5.1 7.8l3.8 1.9" stroke="currentColor"
                          strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                  Compartilhar relatório
                </button>

                {/*
                  Criar ou trocar a senha, sem precisar sair (05/10). Atrás da
                  mesma chave das telas de entrada: com o login por senha
                  desligado, uma senha não serviria para nada.
                */}
                {LOGIN_COM_SENHA && (
                  <button
                    className="app-nav-avatar-menu-item so-com-conta"
                    onClick={() => { setAvatarMenuOpen(false); setShowTrocarSenha(true); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <rect x="2.5" y="6" width="9" height="6" rx="1.4" stroke="currentColor" strokeWidth="1.3"/>
                      <path d="M4.5 6V4.5a2.5 2.5 0 0 1 5 0V6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                    Senha de acesso
                  </button>
                )}

                {/*
                  O interruptor dos avisos no celular.
                  
                  Aqui, e não num modal que aparece sozinho: a permissão do
                  navegador só se pede UMA vez — negada, nem o app nem ela
                  conseguem reverter sem ir nas configurações do sistema. Um
                  pedido que nasce de um toque dela tem a menor chance possível
                  de virar um "bloquear" por reflexo.
                  
                  Some quando o navegador não faz push (o Safari fora da tela
                  inicial, por exemplo): oferecer o que não funciona ali é
                  prometer e falhar.
                */}
                {/*
                  Instalar, pedido por ela.
                  
                  Só aparece quando dá para instalar e o app ainda não está
                  instalado — um item que abre um modal dizendo "já está
                  instalado" é um item que não devia estar ali.
                  
                  No Android o item some depois de a caixa do navegador ser
                  recusada: o evento de instalação serve UMA vez, e o Chrome só
                  o oferece de novo numa carga futura da página. Preferi o item
                  sumir a ele ficar e não fazer nada.
                */}
                {(instalacao.podeInstalar || instalacao.ensinaNoIOS) && (
                  <button
                    className="app-nav-avatar-menu-item so-com-conta"
                    onClick={() => { setAvatarMenuOpen(false); setConvitePedido(true); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1.5v7m0 0L4.5 6M7 8.5L9.5 6"
                            stroke="currentColor" strokeWidth="1.3"
                            strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 10v1.5A1 1 0 003 12.5h8a1 1 0 001-1V10"
                            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                    Adicionar app no {aparelho}
                  </button>
                )}

                {/*
                  Os avisos só aparecem para quem está NO APP.
                  
                  `instalacao.instalado` é `display-mode: standalone` — verdadeiro
                  quando a pessoa abriu pelo ícone, e falso numa aba do
                  navegador. É o que ela pediu: "receber aviso no celular devia
                  aparecer só se eu tiver o app no celular".
                  
                  E é tecnicamente o certo, não só preferência: no iPhone a
                  inscrição de push SÓ existe dentro do app instalado. Oferecer
                  numa aba do Safari seria oferecer o que não funciona ali.
                  
                  A inscrição também é por CONTEXTO: ligar na aba e ligar no app
                  instalado geram inscrições diferentes. Amarrar ao app deixa uma
                  só, no lugar onde a notificação faz sentido.
                */}
                {instalacao.instalado && avisos.estado !== 'indisponivel' && (
                  <button
                    className="app-nav-avatar-menu-item so-com-conta"
                    disabled={avisos.ocupado || avisos.estado === 'bloqueado'
                      || avisos.estado === 'servidor-sem-chave'}
                    onClick={async () => {
                      if (avisos.estado !== 'ligado') { await avisos.ligar(); return; }
                      /*
                       * Desligar pergunta antes (22/10), e a caixa traz o
                       * horário: muita gente desliga porque o aviso chega na
                       * hora errada, e mudar a hora resolve sem perder os avisos.
                       */
                      setAvatarMenuOpen(false);
                      const ok = await confirmar({
                        titulo: `Desligar avisos no ${aparelho}?`,
                        texto: (
                          <>
                            Você para de receber aqui os lembretes de vencimento e os avisos do Finn.
                            Se o problema é a hora, dá para mudar:
                            <HorarioDoAviso naCaixa userId={user?.id} />
                          </>
                        ),
                        acao: 'Desligar',
                        perigo: true,
                        desistir: 'Manter ligados',
                      });
                      if (ok) await avisos.desligar();
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1.5a3.5 3.5 0 013.5 3.5c0 3 1.5 4 1.5 4H2s1.5-1 1.5-4A3.5 3.5 0 017 1.5z"
                            stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                      <path d="M5.6 11a1.5 1.5 0 002.8 0"
                            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                    {avisos.estado === 'bloqueado'
                      ? 'Avisos bloqueados no navegador'
                      : avisos.estado === 'servidor-sem-chave'
                        // Só ela vê isto na prática, e só ela pode resolver:
                        // as chaves VAPID estão no TODOS.md.
                        ? 'Avisos ainda não configurados no servidor'
                        : avisos.estado === 'ligado'
                          ? `Desligar avisos no ${aparelho}`
                          : `Receber avisos no ${aparelho}`}
                  </button>
                )}
                {/* O horário saiu daqui em 22/10: mora na caixa de "Desligar
                    avisos", logo acima. */}
                {/* Só para quem está em ADMIN_EMAILS: são 9 contas neste
                    banco, e atividade dos outros não é dado que qualquer
                    pessoa logada deva ver. O item nem aparece — mostrar e
                    negar no clique seria pior que não mostrar. */}
                {ehAdmin && (
                  <button
                    className="app-nav-avatar-menu-item"
                    // Em outra aba (27/10, pedido dela): o monitor é consulta, e
                    // voltar ao app não pode custar recarregar tudo.
                    onClick={() => { setAvatarMenuOpen(false); window.open(CAMINHO_DA_SAUDE, '_blank', 'noopener'); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M1 7h2.5l2-4.5 3 9L11 7h2" stroke="currentColor"
                            strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Saúde do app
                  </button>
                )}
                {/* Só quem tem plano pago (22/10): a dona e a demo não pagam, e não veem. */}
                {situacaoDoPlano?.cobrando && (situacaoDoPlano.estado === 'liberado' || situacaoDoPlano.estado === 'vencido') && (
                  <button
                    className="app-nav-avatar-menu-item"
                    onClick={() => { setAvatarMenuOpen(false); setShowMeuPlano(true); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <rect x="1.5" y="3" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M1.5 6h11" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    Meu plano
                  </button>
                )}
                <button
                  className="app-nav-avatar-menu-item"
                  onClick={() => { setAvatarMenuOpen(false); if (passosDoComeco) setShowComece(true); else onShowTutorial(); }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.6"/>
                    <path d="M7 9.5V7M7 4.5v.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                  </svg>
                  Comece por aqui
                </button>
                <div className="app-nav-avatar-divider" />
                <button
                  className="app-nav-avatar-signout"
                  onClick={() => { setAvatarMenuOpen(false); onSignOut(); }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5 2H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3M9 10l3-3-3-3M12 7H5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {MODO_DEMO ? 'Sair da demonstração' : 'Sair da conta'}
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile layout modal */}
      {mobileLayoutOpen && (
        <MobileLayoutModal
          sections={secoes.map(id => ({ id, label: SECTION_META[id].label }))}
          onApply={handleMobileLayoutApply}
          onClose={() => setMobileLayoutOpen(false)}
        />
      )}

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <>
          {/*
            O véu. O menu já fechava ao tocar fora (`useFecharAoClicarFora`),
            mas nada na tela DIZIA que o resto estava esperando — a lista
            aparecia por cima do painel como se fosse mais um pedaço dele.
          */}
          <div className="app-mobile-veu" />
          <nav className="app-mobile-menu" ref={menuRef} aria-label="Seções">
            {itensDoMenu.map(item => {
              const Icone = SECTION_ICON[item.id];
              return (
                <button key={item.id} className="app-mobile-link" onClick={() => handleNavClick(item.id)}>
                  <span className="app-mobile-link-icone"><Icone size={18} strokeWidth={1.9} /></span>
                  {item.label}
                </button>
              );
            })}
          </nav>
        </>
      )}

      {editMode && (
        <div className="edit-mode-banner">
          <span className="edit-mode-banner-text">
            ✦ Modo Edição: arraste as seções para reorganizar o layout. Só vale depois de Concluir; o ✕ desfaz.
          </span>
        </div>
      )}

      <div className="container">
        <div className="summary-cards-header">
          <MesDaTela
            mesSelecionado={activeMonth.key}
            mesAtual={currentMonthKey}
            rotulo={activeMonth.label}
            onNavegar={navigateMonth}
            onEscolher={goToMonth}
            onHoje={() => setSelectedProjectionMonth(null)}
          />
        </div>
        {/*
          O aviso de que o servidor não respondeu.

          Fica ACIMA dos cards de propósito: o traço neles diz que o número não
          veio, mas não diz por quê, e "—" sozinho lê como bug. Uma linha
          explicando transforma o mesmo traço em informação.

          Com botão de tentar de novo, porque a causa mais comum é o Render
          free acordando — some sozinho em alguns segundos, e recarregar a
          página inteira para descobrir isso é caro.
        */}
        {falhaDeCarga && (
          <div className="carga-falhou" role="status">
            <span>
              Não consegui falar com o servidor. O que está na tela pode estar
              incompleto, mas nada foi perdido.
            </span>
            <button onClick={() => { loadTransactions(); setProjectionRefreshTrigger(Date.now()); }}>
              Tentar de novo
            </button>
          </div>
        )}
        <div className="summary-cards">
          <div className="summary-card income" style={{ cursor: 'pointer' }} onClick={() => { setTransactionTypeFilter(TransactionType.INCOME); scrollTo('sec-transactions'); }}>
            <div className="summary-card-icon">↑</div>
            <div className="summary-card-content">
              <h3>{comMes('Recebimentos')}</h3>
              <p className="amount">{fmtLocal(totalIncome)}</p>
            </div>
          </div>
          <div className="summary-card expense" style={{ cursor: 'pointer' }} onClick={() => { setTransactionTypeFilter(TransactionType.EXPENSE); scrollTo('sec-transactions'); }}>
            <div className="summary-card-icon">↓</div>
            <div className="summary-card-content">
              <h3>{comMes('Gastos')}</h3>
              <p className="amount">{fmtLocal(totalExpense)}</p>
            </div>
          </div>
          {/*
            O saldo segue o mês, como Recebimentos e Gastos já seguiam.
            Ela apontou a incoerência: "não faz sentido gastos e recebimentos
            serem do mês selecionado mas o saldo do mês vigente".

            `saldoExibido` é o número; `saldoMesSel` manda quando ela está fora
            do mês corrente. O TÍTULO diz de qual mês é — sem isso seriam dois
            valores diferentes no mesmo lugar sem explicação, que é o defeito
            que este app passou três sessões consertando.
          */}
          <div
            id="summary-card-saldo"
            className={`summary-card balance${
              saldoExibido == null ? '' : saldoExibido >= 0 ? ' positive' : ' negative'
            }${saldoCarregando ? ' aguardando' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => { setTransactionTypeFilter('all'); scrollTo('sec-transactions'); }}
          >
            {/* O ícone também espera: com o cálculo local, um mês de despesa
                alta mostrava "!" em vermelho e virava "=" verde meio segundo
                depois — a cor errada assusta antes de o número aparecer. */}
            <div className="summary-card-icon">{saldoExibido == null ? '·' : saldoExibido >= 0 ? '=' : '!'}</div>
            <div className="summary-card-content">
              {/* O MESMO saldo de Projeções e da faixa.
                  Antes, enquanto o servidor não respondia, este card caía num
                  cálculo local — `totalIncome − totalExpense` do mês, que é
                  outro número. Ele aparecia com confiança e trocava meio
                  segundo depois. Agora espera: traço discreto até o valor
                  certo chegar. */}
              <h3>{mesDoTitulo ? `Saldo de ${mesDoTitulo}` : 'Saldo atual'}</h3>
              {/*
                O saldo mostra o que de FATO saiu da conta: conta ligada a
                lembrete e ainda não confirmada não é descontada, porque o
                dinheiro continua lá.

                A projeção NÃO usa este número — ela conta o compromisso
                independente da confirmação. Os dois divergem de propósito, e
                a linha de baixo NOMEIA a diferença. Este app já teve três
                "saldo" discordando em silêncio; o problema nunca foi
                divergir, foi esconder.
              */}
              {valorDoServidor(saldoExibido)}
            </div>
          </div>
          <div
            className={`summary-card invested${saldoCarregando ? ' aguardando' : ''}`}
            onClick={() => scrollTo('sec-investments')}
          >
            <div className="summary-card-icon">◈</div>
            <div className="summary-card-content">
              {/*
                Este segue o mês só PARA FRENTE, e a assimetria é a informação.

                Para o futuro o número existe e é honesto: aplicações rendendo
                juro composto mais reservas crescendo pela sobra — a mesma
                composição do valor de hoje, projetada por `buildGoalTimeline`.

                Para trás não existe. O app guarda o valor ATUAL de cada
                aplicação, não o histórico: não há como saber quanto estava
                aplicado em maio. Aí o título fica "Investido" e o número é o
                de hoje — em vez de "Investido em maio" sobre um valor que não
                é de maio, que seria o rótulo mentindo.
              */}
              <h3>{investidoDoMes != null ? `Investido em ${mesDoTitulo}` : 'Investido'}</h3>
              {valorDoServidor(investidoDoMes ?? totalInvested)}
              {/* O rendimento mensal é o de HOJE. Sobre um valor projetado
                  ele seria a taxa de agora aplicada a um saldo futuro — dois
                  momentos no mesmo número. */}
              {investidoDoMes == null && totalMonthlyReturn > 0 && (
                <p className="summary-card-sub">{hideValues ? maskedValue : `+${fmt(totalMonthlyReturn)} / mês`}</p>
              )}
            </div>
          </div>
          {/*
            O que o saldo atual ainda não descontou — numa linha EMBAIXO dos
            cards, com a seta apontando para ele (15/09).

            Morava dentro do card, em duas linhas: com toda mensal e toda
            compra de cartão aguardando confirmação, os dois valores ficaram
            longos, o card esticou e quebrou a fileira. Pedido dela: "prefiro
            uma linha embaixo com essas informações... e uma seta apontando
            que é uma observação sobre o saldo atual".

            Os dois lados continuam NOMEADOS: um está na conta e ainda não
            saiu, o outro ainda não chegou — somar esconderia qual é qual.
            Só no mês corrente: num mês futuro nada venceu, e num passado o
            número seria de hoje sobre outro mês.
          */}
          {!saldoMesSel && (!!saldo?.aguardandoPagamento || !!saldo?.aguardandoRecebimento) && (
            <p className="summary-saldo-nota">
              <span className="summary-saldo-nota-seta" aria-hidden="true">↑</span>
              <span>
                {/* Cada valor é um botão: tocar mostra em Transações o que o compõe. */}
                {!!saldo?.aguardandoPagamento && (
                  <button type="button" className="summary-saldo-nota-link" onClick={() => verAguardando('pagar')}>
                    {hideValues ? maskedValue : fmt(saldo.aguardandoPagamento)} a pagar
                  </button>
                )}
                {!!saldo?.aguardandoPagamento && !!saldo?.aguardandoRecebimento && ' e '}
                {!!saldo?.aguardandoRecebimento && (
                  <button type="button" className="summary-saldo-nota-link" onClick={() => verAguardando('receber')}>
                    {hideValues ? maskedValue : fmt(saldo.aguardandoRecebimento)} a receber
                  </button>
                )}
                , ainda não confirmado
              </span>
            </p>
          )}
        </div>

        {/*
          A faixa de dicas fica aqui, e fora do DnD.

          Fora do DnD porque `loadOrder` acrescenta seções novas no FIM da
          ordem já salva: como seção arrastável, ela nasceria no rodapé de
          quem já usa o app — que é justamente quem menos precisa dela.

          Este lugar já foi da `BalanceStrip`, apagada em 27/08 por repetir o
          saldo. O que a substitui não repete número nenhum: ela ensina o que
          o app faz e acende a borda de onde clicar.
        */}
        {passosDoComeco && !comeceDispensado && !MODO_DEMO && (
          <ComeceAquiCard
            passos={passosDoComeco}
            onAcao={fazerPasso}
            onVerTudo={() => setShowComece(true)}
            onDispensar={dispensarComece}
          />
        )}

        <DicaFaixa />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={secoes} strategy={rectSortingStrategy}>
            <div className={`sections-grid${editMode ? ' sections-grid-edit' : ''}`}>
              {secoes.map(id => (
                <SortableSection
                  key={id}
                  id={id}
                  full={fullMap[id] ?? SECTION_META[id].full}
                  label={SECTION_META[id].label}
                  editMode={editMode}
                  /*
                   * `sec-form` fica ABERTO no mobile.
                   *
                   * Antes ele nascia colapsado e virava um botão "Nova
                   * Transação". Isso escondia o campo único atrás de um toque,
                   * e o botão de foto junto com ele — enquanto o
                   * `TransactionForm` aberto já mostra os dois no cabeçalho,
                   * com o formulário de 8 campos ainda fechado.
                   *
                   * E editar ficava sem saída: `handleEdit` rolava até a
                   * seção, que continuava colapsada, então ela chegava num
                   * botão em vez do formulário preenchido.
                   *
                   * Desde 23/10 ABRE por padrão, mas se ela fechar, fica
                   * fechado (a edição no celular já é modal).
                   */
                  alwaysExpandedOnMobile={id === 'sec-form'}
                  onToggleFull={() => handleToggleFull(id)}
                >
                  {renderContent(id)}
                </SortableSection>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Flutuante, fora do fluxo do board e do DnD. */}
      {/*
        O chat recebe COMO abrir o WhatsApp, não o modal.

        O marcador do Finn pode convidar a ligar o WhatsApp, e o botão dele
        precisa abrir um modal que vive aqui, não lá. Passar a função mantém
        um dono só para o modal — dois lugares renderizando `WhatsappModal`
        dariam dois estados de "está aberto" e um deles ficaria preso.
      */}
      <AdvisorChat
        refreshTrigger={projectionRefreshTrigger}
        onAbrirWhatsapp={() => setShowWhatsapp(true)}
        onAbrirConector={() => setShowConector(true)}
        abrirCom={perguntaAoFinn}
        pergunta={portasDoFinn && {
          portas: portasDoFinn,
          nome: user?.user_metadata?.full_name ?? user?.user_metadata?.name ?? '',
          onDeclarou: () => {
            setPortasDoFinn(null);
            /*
             * Declarar com alvo CRIA uma meta, e o card de metas foi carregado
             * antes da resposta. Sem isto ela declara "reserva de R$ 12.540" e
             * o card continua vazio até alguém recarregar a página.
             */
            setGoalsRefreshTrigger(Date.now());
          },
          /*
           * Não declarou nada. O Finn volta a perguntar em trinta dias, e quem
           * conta é o servidor. Se o aviso não chegar, a pergunta volta na
           * próxima abertura do app.
           */
          onDepois: () => { setPortasDoFinn(null); adiarPerguntaDoFinn().catch(() => {}); },
        }}
      />

      {editandoEmModal && (
        <EdicaoModal
          titulo="Editar lançamento"
          onFechar={handleCancelEdit}
          voltarPara={rolagemAoEditar.current}
        >
          {formDeTransacao}
        </EdicaoModal>
      )}

      {/* Espera a tela de carga sair: por baixo dela, os botões não recebem o toque (ver o convite de instalar). */}
      {showComece && passosDoComeco && !saidaDaCarga.visivel && (
        <ComeceAquiModal
          nome={user?.user_metadata?.full_name ?? user?.user_metadata?.name ?? ''}
          passos={passosDoComeco}
          observacao={observacaoDoComece}
          onAcao={fazerPasso}
          onTour={() => { setShowComece(false); onShowTutorial(); }}
          onFechar={() => setShowComece(false)}
        />
      )}
      {showWhatsapp && <WhatsappModal userId={user?.id} onClose={() => setShowWhatsapp(false)} />}
      {showConector && <ConectorModal userId={user?.id} onClose={() => setShowConector(false)} />}
      {showNewsletter && <NewsletterModal userId={user?.id} onClose={() => setShowNewsletter(false)} />}
      {showBanco && (
        <CaixaDoBanco
          onClose={() => setShowBanco(false)}
          onVerPlano={() => { setShowBanco(false); setShowMeuPlano(true); }}
          categories={categories}
          paymentMethods={paymentMethods}
          transactions={transactions}
          hideValues={hideValues}
          /* Um movimento aceito é um lançamento novo: lista, saldo e projeção recontam. */
          onLancou={() => { loadTransactions(); setProjectionRefreshTrigger(Date.now()); }}
          onMetodosMudaram={() => { loadPaymentMethods(); setPaymentMethodsRefresh(Date.now()); }}
          onCategoriasMudaram={loadCategories}
          /* O MESMO número do card "Saldo atual": é com ele que o do banco se compara. */
          saldoDoApp={saldo?.saldoConfirmado ?? saldo?.currentBalance ?? null}
          onUsarComoSaldo={handleBalanceAdjust}
          admin={ehAdmin}
          dadosIniciais={caixaPrevia}
        />
      )}
      <Toast message={erroToast} onDismiss={() => setErroToast(null)} />
      {showMeuPlano && <MeuPlanoModal userId={user?.id} onClose={() => setShowMeuPlano(false)} />}
      {showCompartilhar && <CompartilharRelatorio userId={user?.id} onClose={() => setShowCompartilhar(false)} />}
      {showTrocarSenha && <TrocarSenha onClose={() => setShowTrocarSenha(false)} />}

      {/*
        Depois de ligar, onde ajustar.
        
        A permissão do navegador faz o aviso CHEGAR; ela não decide se ele toca,
        aparece por cima da tela ou vibra — isso é o canal do sistema, e em
        vários aparelhos ele nasce silencioso. Não há API para mudar aquilo, e o
        que dá é dizer onde fica.
        
        Sem isso o desfecho é o pior possível: a pessoa liga, não vê nada
        acontecer, e conclui que não funciona.
      */}
      {avisos.testeSaiu !== null && (
        <ModalDeAvisos enviado={avisos.testeSaiu} onFechar={avisos.limparTeste} />
      )}

      {/*
        ── O convite espera a tela de carga sair ────────────────────────────
        Relato dela: "o modal de adicionar na tela inicial no mobile, os botões
        não tão indo" — nenhum deles, nem o ✕, nem tocar fora.

        `convitePodeAparecer` é calculado no inicializador do `useState`, então
        o convite entra JÁ NO PRIMEIRO RENDER — junto com a tela de carga, que
        é `position: fixed`, `inset: 0` e **z-index 9999** contra os 1000 do
        modal. Ela só ganha `pointer-events: none` no estado `saindo`.

        Medido no navegador, com o mesmo CSS:

            sem tela de carga        → o toque chega em `.mi-btn-principal`
            com ela ATIVA            → o toque para em `.app-loading-overlay`
            com ela `saindo`         → volta a chegar no botão

        E ela é translúcida com desfoque, de propósito — então o convite ficava
        VISÍVEL atrás dela e completamente inerte. Não parecia coberto;
        parecia quebrado.

        Localmente nunca aparecia: com sessão falsa a carga é instantânea, e a
        janela de sobreposição não existe. Ela precisa de dados vindo pela rede
        do celular para durar segundos.

        O `!saidaDaCarga.visivel` conserta a causa, não o sintoma: convidar
        alguém a instalar um app que ainda está carregando é errado mesmo que
        os botões funcionassem. `convitePedido` fica de fora da guarda — se ela
        PEDIU pelo menu, a carga já acabou, e negar seria ignorar o pedido.
      */}
      {(convitePedido
        || (convitePodeAparecer && !conviteFechado && telaEstreita && !saidaDaCarga.visivel && !showComece))
        && (instalacao.podeInstalar || instalacao.ensinaNoIOS) && (
        <ModalDeInstalar
          ensinando={instalacao.ensinaNoIOS}
          contaComoRecusa={!convitePedido}
          onFechar={() => { setConviteFechado(true); setConvitePedido(false); }}
        />
      )}

      {mostrarRecomeco && janela && (
        <RestartModal
          janela={janela}
          onDismiss={() => {
            localStorage.setItem(RECOMECO_KEY, janela.lastMonthWithData ?? '');
            setJanela(null);
          }}
          onDone={() => {
            setJanela(null);
            loadTransactions();
            setProjectionRefreshTrigger(Date.now());
          }}
        />
      )}

    </div>
  );
}

export default App;
